import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  alpha,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Card,
  Chip,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import RouteRoundedIcon from '@mui/icons-material/RouteRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import LocalGasStationRoundedIcon from '@mui/icons-material/LocalGasStationRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import SkipNextRoundedIcon from '@mui/icons-material/SkipNextRounded';
import SkipPreviousRoundedIcon from '@mui/icons-material/SkipPreviousRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ScreenHeader from 'src/common/ScreenHeader';
import ReactECharts from 'src/common/ReactECharts';
import { APIService } from 'src/api/api';

const STATUS_META = {
  active: {
    label: 'Active Vehicles',
    shortLabel: 'Active',
    color: '#10B981',
    accent: '#065F46'
  },
  idle: {
    label: 'Idle Vehicles',
    shortLabel: 'Idle',
    color: '#F59E0B',
    accent: '#92400E'
  },
  offline: {
    label: 'Offline Vehicles',
    shortLabel: 'Offline',
    color: '#EF4444',
    accent: '#991B1B'
  }
};

const formatRelativeMinutes = (minutes) => {
  if (!Number.isFinite(minutes)) return 'No sync';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours < 24) return remainingMinutes ? `${hours}h ${remainingMinutes}m ago` : `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const formatDuration = (minutes) => {
  if (!Number.isFinite(minutes) || !minutes) return '0 min';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
};

const formatRefreshStatusLabel = (timestamp, nowValue) => {
  if (!timestamp) return 'Live data not refreshed yet';
  const lastRefreshMoment = moment(timestamp);
  const currentMoment = moment(nowValue);
  const elapsedSeconds = Math.max(0, currentMoment.diff(lastRefreshMoment, 'seconds'));

  if (elapsedSeconds < 15) return 'Updated just now';
  if (elapsedSeconds < 60) return `Updated ${elapsedSeconds}s ago`;

  const elapsedMinutes = currentMoment.diff(lastRefreshMoment, 'minutes');
  if (elapsedMinutes < 60) return `Updated ${elapsedMinutes} min ago`;

  return `Updated ${lastRefreshMoment.format('DD MMM, hh:mm A')}`;
};

const REPORT_TABS = [
  { value: 'statistics', label: 'Statistics', icon: InsightsRoundedIcon },
  { value: 'track', label: 'Track', icon: RouteRoundedIcon },
  { value: 'trackFuel', label: 'Track + Fuel', icon: TimelineRoundedIcon },
  { value: 'location', label: 'Location', icon: PlaceRoundedIcon },
  { value: 'fuel', label: 'Refueling and Draining', icon: LocalGasStationRoundedIcon }
];

const DATE_TIME_INPUT_FORMAT = 'YYYY-MM-DDTHH:mm';
const DATE_RANGE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last7Days', label: 'Last 7 Days' },
  { value: 'last30Days', label: 'Last 30 Days' }
];

const OFFLINE_SYNC_THRESHOLD_MINUTES = 30;

const getDateTimeRangeForPreset = (preset) => {
  const now = moment();
  if (preset === 'yesterday') {
    const yesterday = now.clone().subtract(1, 'day');
    return {
      fromDateTime: yesterday.clone().startOf('day').format(DATE_TIME_INPUT_FORMAT),
      toDateTime: yesterday.endOf('day').format(DATE_TIME_INPUT_FORMAT)
    };
  }
  if (preset === 'last7Days') {
    return {
      fromDateTime: now.clone().subtract(7, 'days').startOf('minute').format(DATE_TIME_INPUT_FORMAT),
      toDateTime: now.format(DATE_TIME_INPUT_FORMAT)
    };
  }
  if (preset === 'last30Days') {
    return {
      fromDateTime: now.clone().subtract(30, 'days').startOf('minute').format(DATE_TIME_INPUT_FORMAT),
      toDateTime: now.format(DATE_TIME_INPUT_FORMAT)
    };
  }
  return {
    fromDateTime: now.clone().startOf('day').format(DATE_TIME_INPUT_FORMAT),
    toDateTime: now.format(DATE_TIME_INPUT_FORMAT)
  };
};

const toUnixSeconds = (dateTimeValue) => {
  const parsedDate = moment(dateTimeValue, DATE_TIME_INPUT_FORMAT, true);
  return parsedDate.isValid() ? parsedDate.unix() : null;
};

const isUnavailableMetric = (value) => {
  if (value === null || value === undefined || value === '') return true;
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? (parsedValue === -10000 || parsedValue === -1) : false;
};

const formatDecimalValue = (value, decimals = 1) => {
  if (isUnavailableMetric(value)) return '--';
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) return '--';
  return parsedValue.toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

const formatTenthsValue = (value, decimals = 1) => {
  if (isUnavailableMetric(value)) return '--';
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) return '--';
  return formatDecimalValue(parsedValue / 10, decimals);
};

const formatHhMmSs = (valueInSeconds) => {
  if (isUnavailableMetric(valueInSeconds)) return '--';
  const totalSeconds = Math.max(0, Math.floor(Number(valueInSeconds)));
  if (!Number.isFinite(totalSeconds)) return '--';
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const formatDurationWithPercent = (seconds, percent) => {
  const durationText = formatHhMmSs(seconds);
  const percentText = formatDecimalValue(percent, 1);
  if (durationText === '--') return '--';
  if (percentText === '--') return durationText;
  return `${durationText} (${percentText} %)`;
};

const normalizeFleetStatistics = (response) => {
  const legacyRoot = response?.result?.data || {};
  const newRoot = response?.result?.results?.data?.[0] || null;

  if (newRoot && Object.keys(newRoot).length) {
    const vehicleData = newRoot?.vehicleDataList?.[0] || null;
    const hasVehicleData = Boolean(vehicleData && Object.keys(vehicleData).length);
    return {
      hasData: hasVehicleData || Boolean(Object.keys(newRoot).length),
      vehicleData: hasVehicleData ? vehicleData : null,
      totals: newRoot
    };
  }

  const vehicleData = legacyRoot?.vehicleDataList?.[0] || null;
  const totals = legacyRoot?.totalFuel || null;
  const hasVehicleData = Boolean(vehicleData && Object.keys(vehicleData).length);
  const hasTotalsData = Boolean(totals && Object.keys(totals).length);

  return {
    hasData: hasVehicleData || hasTotalsData,
    vehicleData: hasVehicleData ? vehicleData : null,
    totals: hasTotalsData ? totals : null
  };
};

const DEFAULT_LOCATION_MAP_CENTER = { lat: 13.0827, lng: 80.2707 };
const LOCATION_MAP_CONTAINER_STYLE = { width: '100%', height: '100%' };
const TRACK_MAP_CONTAINER_STYLE = { width: '100%', height: '100%' };
const TRACK_CLICKABLE_MARKERS_LIMIT = 48;
const TRACK_EVENT_MARKERS_LIMIT = 24;
const TRACK_IGNITION_GROUP_DISTANCE_KM = 0.15;
const TRACK_MAP_PLAYBACK_BASE_INTERVAL_MS = 800;
const TRACK_MAP_PLAYBACK_MIN_INTERVAL_MS = 120;
const TRACK_PLAYBACK_SPEED_OPTIONS = [
  { value: 1, label: '1x' },
  { value: 2, label: '2x' },
  { value: 4, label: '4x' },
  { value: 8, label: '8x' }
];
const FUEL_EVENTS_PAGE_SIZE = 50;
const DEFAULT_FUEL_REPORT_TIMEZONE = 'Asia/Kolkata';
const DEFAULT_FUEL_REPORT_LOCALE = 'en';
const OMNICOMM_UNIX_EPOCH_OFFSET_SECONDS = 1230768000;

const TRACK_EVENT_META = {
  1: { label: 'Refuel start', shortLabel: 'Refuel start', color: '#16A34A' },
  2: { label: 'Refuel end', shortLabel: 'Refuel end', color: '#15803D' },
  3: { label: 'Drain start', shortLabel: 'Drain start', color: '#DC2626' },
  4: { label: 'Drain end', shortLabel: 'Drain end', color: '#B91C1C' },
  5: { label: 'Ignition on', shortLabel: 'Ignition on', color: '#2563EB' },
  6: { label: 'Ignition off', shortLabel: 'Ignition off', color: '#1D4ED8' },
  14: { label: 'Overspeed start', shortLabel: 'Overspeed start', color: '#EA580C' },
  30: { label: 'Overspeed end', shortLabel: 'Overspeed end', color: '#C2410C' },
  42: { label: 'Stop start', shortLabel: 'Stop start', color: '#7C3AED' },
  43: { label: 'Stop end', shortLabel: 'Stop end', color: '#6D28D9' },
  97: { label: 'Parking start', shortLabel: 'Parking start', color: '#0891B2' },
  98: { label: 'Parking end', shortLabel: 'Parking end', color: '#0E7490' },
  100: { label: 'Parking start (ignition)', shortLabel: 'Parking start', color: '#0F766E' },
  13: { label: 'Parking end (ignition)', shortLabel: 'Parking end', color: '#115E59' },
  22: { label: 'Option equipment on', shortLabel: 'Equipment on', color: '#4F46E5' },
  23: { label: 'Option equipment off', shortLabel: 'Equipment off', color: '#4338CA' },
  122: { label: 'Additional equipment on', shortLabel: 'Equipment on', color: '#4F46E5' },
  123: { label: 'Additional equipment off', shortLabel: 'Equipment off', color: '#4338CA' },
  222: { label: 'Additional equipment on', shortLabel: 'Equipment on', color: '#4F46E5' },
  223: { label: 'Additional equipment off', shortLabel: 'Equipment off', color: '#4338CA' },
  322: { label: 'Additional equipment on', shortLabel: 'Equipment on', color: '#4F46E5' },
  323: { label: 'Additional equipment off', shortLabel: 'Equipment off', color: '#4338CA' }
};

const TRACK_IGNITION_EVENT_TYPES = new Set([5, 6]);

const GOOGLE_MAPS_SCRIPT_ID = 'fleet-management-google-maps-script';
const GOOGLE_MAPS_INIT_CALLBACK = '__fleetManagementMapInit';
const MAP_KEY_SESSION_STORAGE_KEY = 'fleet_management_google_maps_key';
const MAP_ID_SESSION_STORAGE_KEY = 'fleet_management_google_map_id';

const isGoogleMapsApiReady = () => Boolean(
  window.google?.maps && typeof window.google.maps.Map === 'function' && typeof window.google.maps.InfoWindow === 'function'
);

const LOCATION_MAP_OPTIONS = {
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: true,
  streetViewControl: false,
  fullscreenControl: true,
  clickableIcons: false,
  gestureHandling: 'cooperative'
};

const LOCATION_MARKER_ICON_DIMENSION = { width: 44, height: 52 };

const getCoordinateValue = (...values) => {
  const matchedValue = values.find((value) => value !== null && value !== undefined && value !== '');
  const parsedValue = Number(matchedValue);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const isValidCoordinatePair = (latitude, longitude) => latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180;

const getVehicleCoordinates = (vehicle) => {
  const latitude = getCoordinateValue(vehicle?.latitude, vehicle?.lat, vehicle?.currentLatitude, vehicle?.currentLat);
  const longitude = getCoordinateValue(vehicle?.longitude, vehicle?.lng, vehicle?.lon, vehicle?.long, vehicle?.currentLongitude, vehicle?.currentLng);

  if (latitude !== null && longitude !== null && isValidCoordinatePair(latitude, longitude)) {
    return { latitude, longitude };
  }
  if (typeof vehicle?.coordinates === 'string') {
    const [rawLat, rawLng] = vehicle.coordinates.split(',').map((value) => Number(value?.trim()));
    if (Number.isFinite(rawLat) && Number.isFinite(rawLng) && isValidCoordinatePair(rawLat, rawLng)) {
      return { latitude: rawLat, longitude: rawLng };
    }
  }
  return null;
};

const formatFuelVolume = (value) => {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) return '--';
  return Number.isInteger(parsedValue) ? `${parsedValue} L` : `${parsedValue.toFixed(1)} L`;
};

const formatSyncDate = (syncAt, fallbackMinutes, format = 'DD MMM YYYY, hh:mm A') => {
  const syncMoment = moment(syncAt);
  if (syncMoment.isValid()) return syncMoment.format(format);
  if (!Number.isFinite(fallbackMinutes)) return 'N/A';
  return moment().subtract(fallbackMinutes, 'minutes').format(format);
};

const formatCoordinateLabel = (value) => {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) return '--';
  return parsedValue.toFixed(5);
};

const getDirectionLabel = (degrees) => {
  const parsedValue = Number(degrees);
  if (!Number.isFinite(parsedValue)) return '--';
  const normalizedValue = ((parsedValue % 360) + 360) % 360;
  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return directions[Math.round(normalizedValue / 45) % directions.length];
};

const getLastSyncMinutes = (eventDate) => {
  const syncMoment = moment(eventDate);
  if (!syncMoment.isValid()) return Number.POSITIVE_INFINITY;
  const minutes = moment().diff(syncMoment, 'minutes');
  return minutes >= 0 ? minutes : 0;
};

const getResolvedGoogleMapId = (mapId) => (typeof mapId === 'string' && mapId.trim() ? mapId.trim() : '');

const getNormalizedTimestampValue = (value) => {
  const parsedValue = Number(value);
  if (Number.isFinite(parsedValue)) {
    if (parsedValue > 1e12) return parsedValue;
    if (parsedValue > 1e9) return parsedValue * 1000;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsedMoment = moment(value);
    if (parsedMoment.isValid()) return parsedMoment.valueOf();
  }
  return null;
};

function getNormalizedOmnicommTimestampValue(value) {
  const parsedValue = Number(value);
  if (Number.isFinite(parsedValue)) {
    if (parsedValue > 1e12) return parsedValue;
    if (parsedValue > 1e9) return parsedValue * 1000;
    if (parsedValue >= 0) return (parsedValue + OMNICOMM_UNIX_EPOCH_OFFSET_SECONDS) * 1000;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsedMoment = moment(value);
    if (parsedMoment.isValid()) return parsedMoment.valueOf();
  }
  return null;
}

function getNormalizedTrackTimestampValue(value) {
  return getNormalizedTimestampValue(value) ?? getNormalizedOmnicommTimestampValue(value);
}

const getFirstFiniteNumber = (...values) => {
  for (let index = 0; index < values.length; index += 1) {
    const parsedValue = Number(values[index]);
    if (Number.isFinite(parsedValue)) return parsedValue;
  }
  return null;
};

const normalizeLegacyTrackMetrics = (metrics = []) => {
  const rawFuelDeciliters = getFirstFiniteNumber(metrics?.[0]);
  const speed = getFirstFiniteNumber(metrics?.[1]);
  const distance = getFirstFiniteNumber(metrics?.[2]);
  const direction = getFirstFiniteNumber(metrics?.[5]);

  return {
    rawFuelDeciliters,
    speed,
    distance,
    stopFlag: metrics?.[3] ?? null,
    ignitionFlag: metrics?.[4] ?? null,
    direction
  };
};

const normalizeOmnicommTrackPointFlags = (flags = []) => ({
  rawFuelDeciliters: getFirstFiniteNumber(flags?.[0]),
  stopFlag: flags?.[1] ?? null,
  ignitionFlag: flags?.[2] ?? null
});

const getTrackEventDateValue = (rawTimestamp, normalizedTimestamp) => {
  const parsedRawTimestamp = Number(rawTimestamp);
  if (Number.isFinite(parsedRawTimestamp) && parsedRawTimestamp >= 0) return parsedRawTimestamp;

  const parsedNormalizedTimestamp = Number(normalizedTimestamp);
  if (Number.isFinite(parsedNormalizedTimestamp) && parsedNormalizedTimestamp > 0) {
    return Math.floor(parsedNormalizedTimestamp / 1000);
  }

  return null;
};

const formatTrackTimestamp = (value, fallback = '--') => {
  const normalizedValue = getNormalizedTrackTimestampValue(value);
  if (!normalizedValue) return fallback;
  const timeMoment = moment(normalizedValue);
  return timeMoment.isValid() ? timeMoment.format('DD MMM YYYY, hh:mm A') : fallback;
};

const getTrackEventMeta = (type) => {
  const normalizedType = Number(type);
  return TRACK_EVENT_META[normalizedType] || {
    label: `Event ${normalizedType}`,
    shortLabel: `Event ${normalizedType}`,
    color: '#F97316'
  };
};

const getTrackEventParameterText = (param2) => {
  const parsedValue = Number(param2);
  if (Number.isFinite(parsedValue) && parsedValue !== 0) return `Ref ${parsedValue}`;
  return '';
};

const normalizeTrackEvent = ({
  rawEvent,
  pointIndex,
  eventIndex,
  pointTimestamp,
  pointEventDateValue
}) => {
  if (Array.isArray(rawEvent)) {
    const [type, param1 = null, param2 = null] = rawEvent;
    const normalizedType = Number(type);
    if (!Number.isFinite(normalizedType)) return null;
    const meta = getTrackEventMeta(normalizedType);

    return {
      id: `track-event-${pointIndex + 1}-${eventIndex + 1}`,
      type: normalizedType,
      param1: Number.isFinite(Number(param1)) ? Number(param1) : param1,
      param2: Number.isFinite(Number(param2)) ? Number(param2) : param2,
      label: meta.label,
      shortLabel: meta.shortLabel,
      color: meta.color,
      timestamp: pointTimestamp,
      eventDateValue: pointEventDateValue,
      parameterText: getTrackEventParameterText(param2)
    };
  }

  if (!rawEvent || typeof rawEvent !== 'object') return null;
  const normalizedType = Number(rawEvent.type ?? rawEvent.eventType ?? rawEvent.code);
  if (!Number.isFinite(normalizedType)) return null;
  const meta = getTrackEventMeta(normalizedType);
  const rawTimestamp = rawEvent.timestamp ?? rawEvent.eventDate ?? rawEvent.date ?? pointEventDateValue;
  const normalizedTimestamp = getNormalizedTrackTimestampValue(rawTimestamp) ?? pointTimestamp;
  const eventDateValue = getTrackEventDateValue(rawTimestamp, normalizedTimestamp) ?? pointEventDateValue;

  return {
    id: rawEvent.id || `track-event-${pointIndex + 1}-${eventIndex + 1}`,
    type: normalizedType,
    param1: rawEvent.param1 ?? null,
    param2: rawEvent.param2 ?? rawEvent.value ?? null,
    label: rawEvent.name || rawEvent.label || meta.label,
    shortLabel: rawEvent.shortLabel || meta.shortLabel,
    color: meta.color,
    timestamp: normalizedTimestamp,
    eventDateValue,
    parameterText: getTrackEventParameterText(rawEvent.param2 ?? rawEvent.value)
  };
};

const getFirstObjectMatch = (value, predicate) => {
  if (!value || typeof value !== 'object') return null;
  if (Array.isArray(value)) return value.find((item) => predicate(item)) || null;
  return Object.values(value).find((item) => predicate(item)) || null;
};

const getNullableBoolean = (value) => {
  if (value === true || value === 1 || value === '1') return true;
  if (value === false || value === 0 || value === '0') return false;
  return null;
};

const getTrackMovementLabel = (isStopped, speed) => {
  if (isStopped === true) return 'Stopped';
  if (isStopped === false) return 'Moving';
  return speed > 0 ? 'Moving' : 'Stopped';
};

const getTrackBooleanLabel = (value, truthyLabel, falsyLabel) => {
  if (value === true) return truthyLabel;
  if (value === false) return falsyLabel;
  return '--';
};

const getTrackFuelVolume = (value, rawDeciliters, preferRawDeciliters = false) => {
  const parsedRawDeciliters = Number(rawDeciliters);
  const rawFuelVolume = Number.isFinite(parsedRawDeciliters) ? parsedRawDeciliters / 10 : null;
  const explicitFuelVolume = Number(value);
  if (preferRawDeciliters && rawFuelVolume !== null) return rawFuelVolume;
  if (Number.isFinite(explicitFuelVolume) && explicitFuelVolume > 0) return explicitFuelVolume;
  if (rawFuelVolume !== null) return rawFuelVolume;
  if (Number.isFinite(explicitFuelVolume)) return explicitFuelVolume;
  return null;
};

const isTrackPointLike = (item) => {
  if (Array.isArray(item)) return item.length >= 2;
  if (!item || typeof item !== 'object') return false;

  return Boolean(
    Number.isFinite(Number(item?.latitude))
    || Number.isFinite(Number(item?.lat))
    || Number.isFinite(Number(item?.lt))
    || Number.isFinite(Number(item?.longitude))
    || Number.isFinite(Number(item?.lng))
    || Number.isFinite(Number(item?.lg))
    || item?.timestamp
    || item?.eventDate
    || item?.gpsDataActualDate
    || item?.ed
    || Array.isArray(item?.p)
  );
};

const getTrackVehicleEntry = (response) => {
  const roots = [
    response?.result?.data,
    response?.result?.results?.data?.[0],
    response?.data,
    response?.result,
    response
  ].filter(Boolean);

  const hasTrackShape = (item) => Boolean(
    item
    && typeof item === 'object'
    && (
      Array.isArray(item?.trackPoints)
      || Array.isArray(item?.track)
      || Array.isArray(item?.points)
      || Array.isArray(item?.routePoints)
      || (Array.isArray(item?.data) && item.data.some((trackPoint) => isTrackPointLike(trackPoint)))
      || item?.summary
      || item?.profile
    )
  );

  return roots.find((root) => hasTrackShape(root))
    || roots.map((root) => getFirstObjectMatch(root?.data, hasTrackShape)).find(Boolean)
    || roots.map((root) => getFirstObjectMatch(root, hasTrackShape)).find(Boolean)
    || null;
};

const getTrackLastKnownEntry = (response) => {
  const roots = [
    response?.result?.data,
    response?.result?.results?.data?.[0],
    response?.data,
    response?.result,
    response
  ].filter(Boolean);

  const hasLastKnownShape = (item) => Boolean(
    item
    && typeof item === 'object'
    && (
      Number.isFinite(Number(item?.latitude))
      || Number.isFinite(Number(item?.lat))
      || Number.isFinite(Number(item?.longitude))
      || Number.isFinite(Number(item?.lng))
      || item?.eventDate
      || item?.gpsDataActualDate
    )
  );

  return roots.find((root) => hasLastKnownShape(root))
    || roots.map((root) => getFirstObjectMatch(root?.data, hasLastKnownShape)).find(Boolean)
    || roots.map((root) => getFirstObjectMatch(root, hasLastKnownShape)).find(Boolean)
    || null;
};

const normalizeTrackPoint = (point, index) => {
  if (Array.isArray(point)) {
    const [longitude, latitude, rawTimestamp, metrics = [], rawEvents = [], gpsValidity = null] = point;
    const normalizedTimestamp = getNormalizedOmnicommTimestampValue(rawTimestamp);
    const legacyMetrics = normalizeLegacyTrackMetrics(metrics);
    const fuelVolume = getTrackFuelVolume(null, legacyMetrics.rawFuelDeciliters, true);
    const eventDateValue = getTrackEventDateValue(rawTimestamp, normalizedTimestamp);
    const events = (Array.isArray(rawEvents) ? rawEvents : [])
      .map((rawEvent, eventIndex) => normalizeTrackEvent({
        rawEvent,
        pointIndex: index,
        eventIndex,
        pointTimestamp: normalizedTimestamp,
        pointEventDateValue: eventDateValue
      }))
      .filter(Boolean);

    return {
      id: `track-point-${index + 1}`,
      latitude: getCoordinateValue(latitude),
      longitude: getCoordinateValue(longitude),
      timestamp: normalizedTimestamp,
      rawTimestamp: rawTimestamp ?? null,
      eventDateValue,
      fuelVolume,
      speed: Number.isFinite(legacyMetrics.speed) ? legacyMetrics.speed : 0,
      distance: Number.isFinite(legacyMetrics.distance) ? legacyMetrics.distance : null,
      ignition: getNullableBoolean(legacyMetrics.ignitionFlag),
      direction: legacyMetrics.direction,
      eventCount: events.length,
      satellitesCount: null,
      isStopped: getNullableBoolean(legacyMetrics.stopFlag),
      gpsValid: getNullableBoolean(gpsValidity),
      rawEvents: Array.isArray(rawEvents) ? rawEvents : [],
      events
    };
  }

  if (!point || typeof point !== 'object') return null;

  const rawPointFlags = Array.isArray(point.p) ? point.p : [];
  const pointFlags = normalizeOmnicommTrackPointFlags(rawPointFlags);
  const rawTimestamp = point.timestamp ?? point.eventDate ?? point.gpsDataActualDate ?? point.date ?? point.time ?? point.ed ?? point.trackTime ?? null;
  const normalizedTimestamp = getNormalizedTrackTimestampValue(rawTimestamp);
  const eventDateValue = getTrackEventDateValue(rawTimestamp, normalizedTimestamp);
  const legacyMetrics = normalizeLegacyTrackMetrics(rawPointFlags);
  const fuelVolume = getTrackFuelVolume(point.fuelVolume ?? point.fuel ?? point.fl, pointFlags.rawFuelDeciliters, Array.isArray(point.p));
  const speed = getFirstFiniteNumber(point.speed, point.s, legacyMetrics.speed);
  const distance = getFirstFiniteNumber(point.distance, point.m, point.trackMileage, legacyMetrics.distance);
  const direction = getFirstFiniteNumber(point.direction, point.d, legacyMetrics.direction);
  const satellitesCount = getFirstFiniteNumber(point.satellitesCount, point.satelliteCount);
  const isStopped = point.stop !== undefined
    ? getNullableBoolean(point.stop)
    : getNullableBoolean(pointFlags.stopFlag);
  const ignition = point.ignition ?? point.currentIgn ?? getNullableBoolean(pointFlags.ignitionFlag);
  let rawEvents = [];
  if (Array.isArray(point.events)) rawEvents = point.events;
  else if (Array.isArray(point.e)) rawEvents = point.e;
  const events = rawEvents
    .map((rawEvent, eventIndex) => normalizeTrackEvent({
      rawEvent,
      pointIndex: index,
      eventIndex,
      pointTimestamp: normalizedTimestamp,
      pointEventDateValue: eventDateValue
    }))
    .filter(Boolean);

  return {
    id: point.id || point.uuid || `track-point-${index + 1}`,
    latitude: getCoordinateValue(point.latitude, point.lat, point.lt),
    longitude: getCoordinateValue(point.longitude, point.lng, point.lon, point.long, point.lg),
    timestamp: normalizedTimestamp,
    rawTimestamp,
    eventDateValue,
    fuelVolume,
    speed: Number.isFinite(speed) ? speed : 0,
    distance: Number.isFinite(distance) ? distance : null,
    ignition,
    direction: Number.isFinite(direction) ? direction : null,
    eventCount: events.length || Number(point.eventCount) || 0,
    satellitesCount: Number.isFinite(satellitesCount) ? satellitesCount : null,
    isStopped,
    gpsValid: point.gpsDataValid !== undefined ? Boolean(point.gpsDataValid) : getNullableBoolean(point.v),
    rawEvents,
    events
  };
};

const normalizeTrackLastKnownPosition = (response) => {
  const entry = getTrackLastKnownEntry(response);
  if (!entry) return null;

  const rawPointFlags = Array.isArray(entry.p) ? entry.p : [];
  const legacyMetrics = normalizeLegacyTrackMetrics(rawPointFlags);
  const latitude = getCoordinateValue(entry.latitude, entry.lat, entry.lt);
  const longitude = getCoordinateValue(entry.longitude, entry.lng, entry.lon, entry.long, entry.lg);
  const rawTimestamp = entry.eventDate ?? entry.gpsDataActualDate ?? entry.timestamp ?? entry.ed ?? null;
  const normalizedTimestamp = getNormalizedTrackTimestampValue(rawTimestamp);
  const speed = getFirstFiniteNumber(entry.speed, entry.s, legacyMetrics.speed);
  const fuelVolume = getTrackFuelVolume(entry.fuelVolume ?? entry.fuel ?? entry.fl, legacyMetrics.rawFuelDeciliters, Array.isArray(entry.p));
  const voltage = Number(entry.voltage);
  const direction = getFirstFiniteNumber(entry.direction, entry.d, legacyMetrics.direction);
  const ignition = entry.ignition ?? getNullableBoolean(legacyMetrics.ignitionFlag);

  return {
    latitude,
    longitude,
    timestamp: normalizedTimestamp,
    rawTimestamp,
    eventDateValue: getTrackEventDateValue(rawTimestamp, normalizedTimestamp),
    speed: Number.isFinite(speed) ? speed : 0,
    fuelVolume,
    voltage: Number.isFinite(voltage) ? voltage : null,
    direction: Number.isFinite(direction) ? direction : null,
    ignition: typeof ignition === 'boolean' ? ignition : null,
    gpsDataValid: entry.gpsDataValid !== undefined ? Boolean(entry.gpsDataValid) : getNullableBoolean(entry.v),
    gsmOn: entry.gsmOn !== undefined ? Boolean(entry.gsmOn) : null,
    eventDate: rawTimestamp
  };
};

const normalizeTrackReport = (response) => {
  const entry = getTrackVehicleEntry(response);
  if (!entry) {
    return {
      hasData: false,
      points: [],
      profile: null,
      summary: null
    };
  }

  const rawPoints = entry.track || entry.trackPoints || entry.points || entry.routePoints || entry.data || [];
  const points = (Array.isArray(rawPoints) ? rawPoints : [])
    .map((point, index) => normalizeTrackPoint(point, index))
    .filter((point) => point && Number.isFinite(point.latitude) && Number.isFinite(point.longitude));
  const events = points
    .flatMap((point, pointIndex) => (Array.isArray(point.events) ? point.events : []).map((event) => ({
      ...event,
      point,
      pointIndex
    })))
    .sort((firstEvent, secondEvent) => (firstEvent.timestamp || 0) - (secondEvent.timestamp || 0));

  const derivedProfile = {
    registrationNumber: entry.registrationNumber ?? null,
    vehicleId: entry.vehicleId ?? entry.vehicleID ?? null,
    maximalSpeed: Number.isFinite(Number(entry.maximalSpeed)) ? Number(entry.maximalSpeed) : null,
    unis: Array.isArray(entry.unis) ? entry.unis : []
  };
  const hasDerivedProfile = Object.values(derivedProfile).some((value) => (
    Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined && value !== ''
  ));
  const profile = entry.profile || entry.vehicleProfile || (hasDerivedProfile ? derivedProfile : null);
  const summary = entry.summary || null;

  return {
    hasData: Boolean(points.length || profile || summary || events.length),
    points,
    events,
    profile,
    summary
  };
};

const normalizeFuelLevelSeries = (response) => {
  const reportEntry = response?.result?.results?.data?.[0] || null;
  if (!reportEntry || typeof reportEntry !== 'object') {
    return {
      hasData: false,
      actualSeries: [],
      smoothedSeries: [],
      tankCapacity: null,
      tankNumber: null
    };
  }

  const normalizeSeries = (series) => (Array.isArray(series) ? series : [])
    .map((point) => {
      if (!Array.isArray(point) || point.length < 2) return null;
      const timestamp = Number(point[0]);
      const value = Number(point[1]);
      if (!Number.isFinite(timestamp) || !Number.isFinite(value)) return null;
      return [timestamp, Number(value.toFixed(1))];
    })
    .filter(Boolean);

  const actualSeries = normalizeSeries(reportEntry.rawValues);
  const smoothedSeries = normalizeSeries(reportEntry.approxValues);

  return {
    hasData: Boolean(actualSeries.length || smoothedSeries.length),
    actualSeries,
    smoothedSeries,
    tankCapacity: Number.isFinite(Number(reportEntry.tankCapacity)) ? Number(reportEntry.tankCapacity) : null,
    tankNumber: Number.isFinite(Number(reportEntry.tankNumber)) ? Number(reportEntry.tankNumber) : null
  };
};

const getDistanceBetweenCoordinates = (startPoint, endPoint) => {
  if (!startPoint || !endPoint) return 0;
  const earthRadiusKm = 6371;
  const latitudeDelta = ((endPoint.latitude - startPoint.latitude) * Math.PI) / 180;
  const longitudeDelta = ((endPoint.longitude - startPoint.longitude) * Math.PI) / 180;
  const startLatitude = (startPoint.latitude * Math.PI) / 180;
  const endLatitude = (endPoint.latitude * Math.PI) / 180;

  const haversine = Math.sin(latitudeDelta / 2) ** 2
    + (Math.cos(startLatitude) * Math.cos(endLatitude) * Math.sin(longitudeDelta / 2) ** 2);

  return 2 * earthRadiusKm * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
};

const getTrackDistanceFromPoints = (points) => points.reduce((totalDistance, point, index) => {
  if (index === 0) return totalDistance;
  return totalDistance + getDistanceBetweenCoordinates(points[index - 1], point);
}, 0);

const getTrackMetricDistance = (points) => {
  const distanceValues = points
    .map((point) => Number(point.distance))
    .filter((value) => Number.isFinite(value));

  if (distanceValues.length < 2) {
    return distanceValues.length ? Math.max(distanceValues[0], 0) : getTrackDistanceFromPoints(points);
  }

  const isNonDecreasingSeries = distanceValues.every((value, index) => index === 0 || value >= distanceValues[index - 1]);
  if (isNonDecreasingSeries) {
    return Math.max(0, distanceValues[distanceValues.length - 1] - distanceValues[0]);
  }

  return Math.max(...distanceValues);
};

const getTrackSummary = (trackReport, lastKnownPosition) => {
  const points = trackReport?.points || [];
  const trackEvents = trackReport?.events || [];
  const speeds = points.map((point) => point.speed).filter((value) => Number.isFinite(value));
  const hasStopFlags = points.some((point) => typeof point.isStopped === 'boolean');
  const movingPointsCount = hasStopFlags
    ? points.filter((point) => point.isStopped === false).length
    : points.filter((point) => point.speed > 0).length;
  const startPoint = points[0] || null;
  const endPoint = points[points.length - 1] || null;
  const maxSpeed = speeds.length ? Math.max(...speeds) : 0;
  const totalDistance = getTrackMetricDistance(points);
  const periodMinutes = startPoint?.timestamp && endPoint?.timestamp
    ? Math.max(0, Math.round((endPoint.timestamp - startPoint.timestamp) / 60000))
    : null;

  return {
    totalPoints: points.length,
    totalEvents: trackEvents.length,
    eventPointsCount: points.filter((point) => point.eventCount > 0).length,
    movingPointsCount,
    stoppedPointsCount: hasStopFlags
      ? points.filter((point) => point.isStopped === true).length
      : Math.max(0, points.length - movingPointsCount),
    maxSpeed,
    totalDistance,
    startPoint,
    endPoint,
    currentPoint: lastKnownPosition?.latitude !== null && lastKnownPosition?.longitude !== null ? lastKnownPosition : endPoint,
    periodMinutes
  };
};

const getSampledTrackMarkers = (points, maxMarkers = TRACK_CLICKABLE_MARKERS_LIMIT, excludedPointIds = new Set()) => {
  if (!Array.isArray(points) || points.length <= 2) return [];

  const intermediatePoints = points
    .map((point, pointIndex) => ({ point, pointIndex }))
    .filter(({ point }) => !excludedPointIds.has(point.id))
    .slice(1, -1);

  if (!intermediatePoints.length) return [];

  const step = Math.max(1, Math.ceil(intermediatePoints.length / maxMarkers));
  return intermediatePoints.filter((_, index) => index % step === 0).slice(0, maxMarkers);
};

const getTrackEventMarkers = (points, maxMarkers = TRACK_EVENT_MARKERS_LIMIT) => {
  if (!Array.isArray(points) || points.length <= 2) return [];

  const eventPoints = points
    .map((point, pointIndex) => ({ point, pointIndex }))
    .slice(1, -1)
    .filter(({ point }) => point.eventCount > 0);

  if (!eventPoints.length) return [];

  const step = Math.max(1, Math.ceil(eventPoints.length / maxMarkers));
  return eventPoints.filter((_, index) => index % step === 0).slice(0, maxMarkers);
};

const getTrackIgnitionEventGroups = (events, distanceThresholdKm = TRACK_IGNITION_GROUP_DISTANCE_KM) => {
  if (!Array.isArray(events) || !events.length) return [];

  const ignitionEvents = events
    .filter((event) => TRACK_IGNITION_EVENT_TYPES.has(Number(event?.type)))
    .filter((event) => Number.isFinite(event?.point?.latitude) && Number.isFinite(event?.point?.longitude));

  if (!ignitionEvents.length) return [];

  const groups = [];
  ignitionEvents.forEach((event) => {
    const eventPoint = {
      latitude: event.point.latitude,
      longitude: event.point.longitude
    };

    const matchedGroup = groups.find((group) => getDistanceBetweenCoordinates(group, eventPoint) <= distanceThresholdKm);

    if (matchedGroup) {
      matchedGroup.events.push(event);
      matchedGroup.lastTimestamp = event.timestamp || matchedGroup.lastTimestamp;
      matchedGroup.ignitionOnCount += event.type === 5 ? 1 : 0;
      matchedGroup.ignitionOffCount += event.type === 6 ? 1 : 0;
      return;
    }

    groups.push({
      id: `track-ignition-group-${groups.length + 1}`,
      latitude: event.point.latitude,
      longitude: event.point.longitude,
      events: [event],
      firstTimestamp: event.timestamp || null,
      lastTimestamp: event.timestamp || null,
      ignitionOnCount: event.type === 5 ? 1 : 0,
      ignitionOffCount: event.type === 6 ? 1 : 0
    });
  });

  return groups
    .map((group) => ({
      ...group,
      eventCount: group.events.length,
      title: `${group.events.length} ignition events`
    }))
    .sort((firstGroup, secondGroup) => (secondGroup.eventCount || 0) - (firstGroup.eventCount || 0));
};

const normalizeLocationMapCardData = (response) => {
  const data = response?.result?.data || response?.data || null;
  if (!data || typeof data !== 'object') return null;

  return {
    address: data.parseAddress || data.address || '--',
    driver: data.driver || '--',
    engineWork: data.engineWork || '--',
    tempFuel: Number.isFinite(Number(data.tempFuel)) ? Number(data.tempFuel) : null,
    voltage: Number.isFinite(Number(data.voltage)) ? Number(data.voltage) : null,
    mileageCAN: Number.isFinite(Number(data.mileageCAN)) ? Number(data.mileageCAN) : null,
    vehicleID: data.vehicleID ?? '--',
    additionalEquipment: Array.isArray(data.additionalEquipment) ? data.additionalEquipment : []
  };
};

const getFuelEventTypeLabel = (type) => {
  const normalizedType = Number(type);
  if (normalizedType === 1) return 'Refilling';
  if (normalizedType === 3) return 'Draining';
  return 'Unknown';
};

const normalizeFuelEventRow = (row, index) => {
  if (!row || typeof row !== 'object') return null;

  const rawVolume = Number(row.volume);
  const [longitude, latitude] = Array.isArray(row.address) ? row.address : [];
  const startDate = row.startdate ?? row.startDate ?? null;
  const endDate = row.enddate ?? row.endDate ?? null;
  const eventDate = row.eventdate ?? row.eventDate ?? endDate ?? startDate;
  const durationMinutes = Number.isFinite(Number(startDate)) && Number.isFinite(Number(endDate))
    ? Math.max(0, Math.round((Number(endDate) - Number(startDate)) / 60000))
    : null;

  return {
    id: row.id ?? `fuel-event-${index + 1}`,
    vehicleName: row.name ?? '--',
    type: Number(row.type),
    typeLabel: getFuelEventTypeLabel(row.type),
    volume: Number.isFinite(rawVolume) ? rawVolume / 10 : null,
    startDate,
    endDate,
    eventDate,
    durationMinutes,
    tankNumber: row.tankNmb ?? '--',
    address: row.parseAddress || '--',
    latitude: getCoordinateValue(latitude),
    longitude: getCoordinateValue(longitude),
    vehicleId: row.vehicleID ?? null
  };
};

const normalizeFuelEventsReport = (response) => {
  const results = response?.result?.results || response?.results || null;
  const rows = Array.isArray(results?.rows)
    ? results.rows.map((row, index) => normalizeFuelEventRow(row, index)).filter(Boolean)
    : [];

  return {
    rows,
    totalPages: Number(results?.total) || 0,
    totalRecords: Number(results?.records) || rows.length,
    currentPage: Number(results?.page) || 1
  };
};

// Render a high-contrast truck badge that stays visible on all map backgrounds.
const buildVehicleMarkerIconSvg = () => (
  `<svg width="44" height="52" viewBox="0 0 44 52" xmlns="http://www.w3.org/2000/svg">
    <circle cx="22" cy="20" r="16" fill="#0F172A"/>
    <circle cx="22" cy="20" r="13" fill="#FFFFFF"/>
    <path d="M22 49 15.5 38h13L22 49z" fill="#0F172A"/>
    <path d="M29.8 24.3c0 1-.8 1.8-1.8 1.8s-1.8-.8-1.8-1.8h-4.5c0 1-.8 1.8-1.8 1.8s-1.8-.8-1.8-1.8H17c-.5 0-.9-.4-.9-.9v-6.2c0-.5.4-.9.9-.9H25v-1.6c0-.5.4-.9.9-.9h3.3c.3 0 .5.1.7.4l2.3 3c.1.2.2.4.2.6v5.6c0 .5-.4.9-.9.9h-1.7zm-9.8 0c0 .4.3.7.7.7s.7-.3.7-.7-.3-.7-.7-.7-.7.3-.7.7zm8 0c0 .4.3.7.7.7s.7-.3.7-.7-.3-.7-.7-.7-.7.3-.7.7zm2.3-6.1-1.6-2.1h-2.2v2.1h3.8z" fill="#0284C7"/>
    <rect x="17.8" y="17.7" width="6.2" height="2.8" rx="0.6" fill="#BAE6FD"/>
  </svg>`
);

const escapeHtml = (value) => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const getMapMarkerInfoContent = (marker) => {
  const statusLabel = escapeHtml(STATUS_META[marker.status]?.shortLabel || 'Live');
  const safePlate = escapeHtml(marker.plate || 'Vehicle');
  return `
    <div style="font-family:Arial,sans-serif;font-size:12px;line-height:1.4;padding:4px 0;">
      <div style="font-weight:700;color:#0F172A;">${safePlate}</div>
      <div style="color:#475569;">${statusLabel}</div>
      <div style="color:#64748B;">Speed: ${escapeHtml(`${marker.speed} km/h`)}</div>
      <div style="color:#64748B;">Fuel: ${escapeHtml(formatFuelVolume(marker.fuelLevel))}</div>
    </div>
  `;
};

const getFuelEventMarkerInfoContent = (row) => {
  const safeType = escapeHtml(row?.typeLabel || '--');
  const safeVehicle = escapeHtml(row?.vehicleName || '--');
  const safeTime = escapeHtml(formatTrackTimestamp(row?.eventDate, '--'));
  const safeVolume = escapeHtml(formatFuelVolume(row?.volume));
  const safeAddress = escapeHtml(row?.address || '--');

  return `
    <div style="font-family:Arial,sans-serif;font-size:12px;line-height:1.4;padding:4px 0;min-width:220px;max-width:280px;">
      <div style="font-weight:700;color:#0F172A;">${safeVehicle}</div>
      <div style="color:#475569;">${safeType}</div>
      <div style="margin-top:6px;color:#334155;"><span style="color:#64748B;">Time:</span> ${safeTime}</div>
      <div style="color:#334155;"><span style="color:#64748B;">Volume:</span> ${safeVolume}</div>
      <div style="margin-top:6px;color:#334155;"><span style="color:#64748B;">Address:</span> ${safeAddress}</div>
    </div>
  `;
};

const getTrackMarkerInfoContent = ({
  title,
  plate,
  speed,
  fuelVolume,
  satellitesCount,
  timestamp,
  direction,
  latitude,
  longitude,
  ignition,
  isStopped,
  gpsValid,
  events = [],
  details,
  isLoading = false,
  error = ''
}) => `
  <div style="font-family:Arial,sans-serif;font-size:11px;line-height:1.35;padding:2px 0;min-width:0;max-width:240px;color:#475569;">
    <div style="font-weight:700;font-size:12px;color:#0F172A;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(title)}</div>
    <div style="margin-top:2px;color:#334155;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(plate)}</div>
    <div style="margin-top:8px;display:grid;grid-template-columns:auto 1fr;gap:4px 10px;align-items:start;">
      <div style="color:#94A3B8;">Time</div>
      <div style="color:#0F172A;">${escapeHtml(formatTrackTimestamp(timestamp))}</div>
      <div style="color:#94A3B8;">Coords</div>
      <div style="color:#0F172A;">${escapeHtml(`${formatCoordinateLabel(latitude)}, ${formatCoordinateLabel(longitude)}`)}</div>
      <div style="color:#94A3B8;">Speed</div>
      <div style="color:#0F172A;">${escapeHtml(`${speed || 0} km/h`)}</div>
      <div style="color:#94A3B8;">Smooth fuel</div>
      <div style="color:#0F172A;">${escapeHtml(formatFuelVolume(fuelVolume))}</div>
      <div style="color:#94A3B8;">Move</div>
      <div style="color:#0F172A;">${escapeHtml(getTrackMovementLabel(isStopped, speed))}</div>
      <div style="color:#94A3B8;">Ignition</div>
      <div style="color:#0F172A;">${escapeHtml(getTrackBooleanLabel(ignition, 'On', 'Off'))}</div>
      <div style="color:#94A3B8;">GPS</div>
      <div style="color:#0F172A;">${escapeHtml(getTrackBooleanLabel(gpsValid, 'Valid', 'Invalid'))}</div>
      <div style="color:#94A3B8;">Heading</div>
      <div style="color:#0F172A;">${escapeHtml(getDirectionLabel(direction))}</div>
      <div style="color:#94A3B8;">Sats</div>
      <div style="color:#0F172A;">${escapeHtml(satellitesCount ?? '--')}</div>
    </div>
    ${events.length ? `
      <div style="margin-top:8px;padding-top:8px;border-top:1px solid #E2E8F0;">
        <div style="margin-bottom:6px;color:#94A3B8;">Events</div>
        <div style="display:flex;flex-direction:column;gap:6px;">
          ${events.slice(0, 5).map((event) => `
            <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:8px;">
              <div style="min-width:0;">
                <div style="display:inline-flex;align-items:center;gap:6px;color:#0F172A;font-weight:700;">
                  <span style="width:8px;height:8px;border-radius:999px;background:${escapeHtml(event.color || '#F97316')};display:inline-block;"></span>
                  <span>${escapeHtml(event.shortLabel || event.label || 'Event')}</span>
                </div>
                ${event.parameterText ? `<div style="color:#64748B;">${escapeHtml(event.parameterText)}</div>` : ''}
              </div>
              <div style="color:#64748B;white-space:nowrap;">${escapeHtml(formatTrackTimestamp(event.timestamp || event.eventDateValue, '--'))}</div>
            </div>
          `).join('')}
        </div>
      </div>
    ` : ''}
    ${isLoading ? '<div style="margin-top:8px;padding-top:8px;border-top:1px solid #E2E8F0;color:#2563EB;font-weight:700;">Loading details...</div>' : ''}
    ${error ? `<div style="margin-top:8px;padding-top:8px;border-top:1px solid #E2E8F0;color:#B91C1C;font-weight:700;">${escapeHtml(error)}</div>` : ''}
    ${!isLoading && !error && details ? `
      <div style="margin-top:8px;padding-top:8px;border-top:1px solid #E2E8F0;display:grid;grid-template-columns:auto 1fr;gap:4px 10px;align-items:start;">
        <div style="color:#94A3B8;">Address</div>
        <div style="color:#0F172A;">${escapeHtml(details.address || '--')}</div>
        <div style="color:#94A3B8;">Engine</div>
        <div style="color:#0F172A;">${escapeHtml(details.engineWork || '--')}</div>
        <div style="color:#94A3B8;">Fuel temp</div>
        <div style="color:#0F172A;">${escapeHtml(details.tempFuel !== null && details.tempFuel !== undefined ? `${details.tempFuel} deg C` : '--')}</div>
        <div style="color:#94A3B8;">Voltage</div>
        <div style="color:#0F172A;">${escapeHtml(details.voltage !== null && details.voltage !== undefined ? `${details.voltage} V` : '--')}</div>
        <div style="color:#94A3B8;">Mileage CAN</div>
        <div style="color:#0F172A;">${escapeHtml(details.mileageCAN !== null && details.mileageCAN !== undefined ? `${details.mileageCAN}` : '--')}</div>
        <div style="color:#94A3B8;">Vehicle ID</div>
        <div style="color:#0F172A;">${escapeHtml(details.vehicleID ?? '--')}</div>
        <div style="color:#94A3B8;">Equipment</div>
        <div style="color:#0F172A;">${escapeHtml(Array.isArray(details.additionalEquipment) && details.additionalEquipment.length ? details.additionalEquipment.map((equipment) => `${equipment?.name || 'Equipment'}: ${equipment?.value ?? '--'}`).join(', ') : '--')}</div>
      </div>
    ` : ''}
  </div>
`;

const getMapZoomByCoordinateSpread = (spread) => {
  if (spread <= 0.002) return 16;
  if (spread <= 0.008) return 15;
  if (spread <= 0.02) return 14;
  if (spread <= 0.05) return 13;
  if (spread <= 0.12) return 12;
  if (spread <= 0.3) return 11;
  if (spread <= 0.8) return 10;
  return 9;
};

const fitLocationMapToMarkers = (mapInstance, markers) => {
  if (!markers.length) return;
  if (markers.length === 1) {
    const onlyMarker = markers[0];
    mapInstance.setCenter({ lat: onlyMarker.latitude, lng: onlyMarker.longitude });
    mapInstance.setZoom(16);
    return;
  }
  const bounds = new window.google.maps.LatLngBounds();
  markers.forEach((marker) => bounds.extend({ lat: marker.latitude, lng: marker.longitude }));
  if (bounds.isEmpty()) return;
  mapInstance.fitBounds(bounds, 64);
  window.google.maps.event.addListenerOnce(mapInstance, 'idle', () => {
    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();
    const spread = Math.max(Math.abs(northEast.lat() - southWest.lat()), Math.abs(northEast.lng() - southWest.lng()));
    const targetZoom = getMapZoomByCoordinateSpread(spread);
    const currentZoom = mapInstance.getZoom();
    if (Number.isFinite(currentZoom) && currentZoom > targetZoom) {
      mapInstance.setZoom(targetZoom);
    }
  });
};

const fitMapToCoordinatePoints = (mapInstance, points) => {
  if (!points.length) return;
  if (points.length === 1) {
    const onlyPoint = points[0];
    mapInstance.setCenter({ lat: onlyPoint.latitude, lng: onlyPoint.longitude });
    mapInstance.setZoom(15);
    return;
  }

  const bounds = new window.google.maps.LatLngBounds();
  points.forEach((point) => bounds.extend({ lat: point.latitude, lng: point.longitude }));
  if (bounds.isEmpty()) return;

  mapInstance.fitBounds(bounds, 72);
  window.google.maps.event.addListenerOnce(mapInstance, 'idle', () => {
    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();
    const spread = Math.max(Math.abs(northEast.lat() - southWest.lat()), Math.abs(northEast.lng() - southWest.lng()));
    const targetZoom = getMapZoomByCoordinateSpread(spread);
    const currentZoom = mapInstance.getZoom();
    if (Number.isFinite(currentZoom) && currentZoom > targetZoom) {
      mapInstance.setZoom(targetZoom);
    }
  });
};

const getVehicleState = ({ lastSyncMinutes, currentIgn, speed }) => {
  if (lastSyncMinutes > OFFLINE_SYNC_THRESHOLD_MINUTES) return 'offline';
  if (currentIgn || speed > 0) return 'active';
  return 'idle';
};

const getVehicleDisplayName = (row, index) => row.vehicleName
  || row.name
  || row.regNumb
  || `Vehicle ${index + 1}`;

const getVehiclePlaceName = (row) => {
  const placeName = [
    row.locationName,
    row.placeName,
    row.address,
    row.location,
    row.currentAddress,
    row.formattedAddress,
    row.siteName,
    row.site,
    row.areaName,
    row.area,
    row.city,
    row.district,
    row.zoneName,
    row.geofenceName
  ].find((value) => typeof value === 'string' && value.trim() && value.trim() !== 'N/A' && value.trim() !== '--');

  return placeName?.trim() || 'Location unavailable';
};

const getVehicleDistance = (row) => {
  const distanceValue = [
    row.todayDistance,
    row.distance,
    row.dayDistance,
    row.totalDistance
  ].find((value) => Number.isFinite(Number(value)));

  return distanceValue ? Math.max(0, Math.round(Number(distanceValue))) : 0;
};

const buildCurrentStateVehicles = (rows = []) => {
  if (!Array.isArray(rows) || !rows.length) return [];

  return rows.map((row, index) => {
    const speed = Number(row.speed) || 0;
    const fuelValue = Number(row.currentFuelVolumeTank1);
    const hasFuel = Number.isFinite(fuelValue);
    const lastSyncMinutes = getLastSyncMinutes(row.eventDate);
    const status = getVehicleState({ lastSyncMinutes, currentIgn: Boolean(row.currentIgn), speed });
    const coordinates = getVehicleCoordinates(row);
    const latitude = coordinates?.latitude ?? null;
    const longitude = coordinates?.longitude ?? null;
    const hasCoordinates = Boolean(coordinates);

    return {
      id: row.uuid || `vehicle-${index + 1}`,
      plate: row.regNumb || `Vehicle ${index + 1}`,
      name: getVehicleDisplayName(row, index),
      placeName: getVehiclePlaceName(row),
      status,
      speed,
      fuelLevel: hasFuel ? fuelValue : 0,
      todayDistance: getVehicleDistance(row),
      lastSyncAt: row.eventDate || null,
      latitude: hasCoordinates ? latitude : null,
      longitude: hasCoordinates ? longitude : null,
      coordinates: hasCoordinates ? `${latitude.toFixed(5)}, ${longitude.toFixed(5)}` : 'N/A',
      idleMinutes: status === 'idle' ? lastSyncMinutes : 0
    };
  });
};

const VehicleListItem = ({
  vehicle, selected, onClick
}) => {
  const statusMeta = STATUS_META[vehicle.status];
  const syncLabel = formatRelativeMinutes(vehicle.lastSyncMinutes);
  const hoverDetails = (
    <Box sx={{ p: 0.75 }}>
      <Typography variant="subtitle2" sx={{ color: '#FFFFFF', fontWeight: 800 }}>{vehicle.plate}</Typography>
      <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.78)' }}>
        Status:
        {' '}
        {statusMeta.shortLabel}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.78)' }}>
        Last sync:
        {' '}
        {formatSyncDate(vehicle.lastSyncAt, vehicle.lastSyncMinutes, 'DD MMM, hh:mm A')}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', mt: 0.45, color: '#E2E8F0' }}>
        Speed:
        {' '}
        {vehicle.speed}
        {' '}
        km/h
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: '#E2E8F0' }}>
        Fuel:
        {' '}
        {formatFuelVolume(vehicle.fuelLevel)}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: '#E2E8F0' }}>
        Distance:
        {' '}
        {vehicle.todayDistance}
        {' '}
        km
      </Typography>
    </Box>
  );

  return (
    <Tooltip title={hoverDetails} placement="right" enterDelay={250} arrow>
      <Box>
        <ButtonBase
          onClick={onClick}
          sx={{
            width: '100%', borderRadius: 2.5, display: 'block', textAlign: 'left'
          }}
        >
          <Card
            sx={{
              p: 1,
              borderRadius: 2.5,
              border: `1px solid ${selected ? alpha(statusMeta.color, 0.28) : 'rgba(148, 163, 184, 0.2)'}`,
              boxShadow: 'none',
              bgcolor: selected ? alpha(statusMeta.color, 0.06) : '#FFFFFF',
              transition: 'background-color 150ms ease, border-color 150ms ease',
              '&:hover': { bgcolor: alpha(statusMeta.color, 0.04), borderColor: alpha(statusMeta.color, 0.24) }
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  flexShrink: 0,
                  bgcolor: statusMeta.color,
                  boxShadow: selected ? `0 0 0 4px ${alpha(statusMeta.color, 0.18)}` : 'none'
                }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 800, lineHeight: 1.25 }}>
                  {vehicle.plate}
                </Typography>
                <Typography noWrap variant="caption" sx={{ display: 'block', mt: 0.2, color: '#64748B' }}>
                  {vehicle.name}
                </Typography>
              </Box>
              {syncLabel !== 'No sync' && (
                <Typography variant="caption" sx={{ color: '#475569', fontWeight: 700, flexShrink: 0 }}>
                  {syncLabel}
                </Typography>
              )}
            </Stack>
          </Card>
        </ButtonBase>
      </Box>
    </Tooltip>
  );
};

VehicleListItem.propTypes = {
  vehicle: PropTypes.object.isRequired,
  selected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired
};

const DetailMetricCard = ({
  label, value, caption, icon: Icon, color
}) => (
  <Card
    sx={{
      p: 1.75, height: '100%', borderRadius: 3, border: `1px solid ${alpha(color, 0.14)}`, boxShadow: 'none', bgcolor: alpha(color, 0.05)
    }}
  >
    <Stack direction="row" justifyContent="space-between" spacing={1.25}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>{label}</Typography>
        <Typography variant="h6" sx={{ mt: 0.75, fontWeight: 800, color: '#0F172A' }}>{value}</Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#475569' }}>{caption}</Typography>
      </Box>
      <Avatar
        variant="rounded"
        sx={{
          width: 40, height: 40, bgcolor: alpha(color, 0.15), color
        }}
      >
        <Icon fontSize="small" />
      </Avatar>
    </Stack>
  </Card>
);

DetailMetricCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  caption: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  color: PropTypes.string.isRequired
};

const FleetManagement = () => {
  const theme = useTheme();

  const [vehicles, setVehicles] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [selectedVehicleIds, setSelectedVehicleIds] = useState([]);
  const [activeReport, setActiveReport] = useState('statistics');
  const [dateRangePreset, setDateRangePreset] = useState('today');
  const [dateRange, setDateRange] = useState(() => getDateTimeRangeForPreset('today'));
  const [isVehicleLoading, setVehicleLoading] = useState(false);
  const [isFleetLoading, setFleetLoading] = useState(true);
  const [isRefreshingFleet, setRefreshingFleet] = useState(false);
  const [lastFleetRefreshAt, setLastFleetRefreshAt] = useState(null);
  const [refreshStatusTick, setRefreshStatusTick] = useState(Date.now());
  const [statisticsData, setStatisticsData] = useState(null);
  const [isStatisticsLoading, setStatisticsLoading] = useState(false);
  const [statisticsError, setStatisticsError] = useState('');
  const [fuelEventsData, setFuelEventsData] = useState(null);
  const [isFuelEventsLoading, setFuelEventsLoading] = useState(false);
  const [fuelEventsError, setFuelEventsError] = useState('');
  const [selectedFuelEventMapRow, setSelectedFuelEventMapRow] = useState(null);
  const [fuelEventMapError, setFuelEventMapError] = useState('');
  const [trackData, setTrackData] = useState(null);
  const [isTrackLoading, setTrackLoading] = useState(false);
  const [trackError, setTrackError] = useState('');
  const [trackFuelData, setTrackFuelData] = useState(null);
  const [isTrackFuelLoading, setTrackFuelLoading] = useState(false);
  const [trackFuelError, setTrackFuelError] = useState('');
  const [selectedTrackPoint, setSelectedTrackPoint] = useState(null);
  const [selectedTrackEventGroup, setSelectedTrackEventGroup] = useState(null);
  const [trackPlaybackIndex, setTrackPlaybackIndex] = useState(0);
  const [isTrackPlaybackActive, setTrackPlaybackActive] = useState(false);
  const [trackPlaybackSpeed, setTrackPlaybackSpeed] = useState(4);
  const [selectedTrackPointDetails, setSelectedTrackPointDetails] = useState(null);
  const [selectedTrackPointDetailsLoading, setSelectedTrackPointDetailsLoading] = useState(false);
  const [selectedTrackPointDetailsError, setSelectedTrackPointDetailsError] = useState('');
  const [googleMapsKey, setGoogleMapsKey] = useState('');
  const [googleMapsMapId, setGoogleMapsMapId] = useState('');
  const [isMapKeyLoading, setMapKeyLoading] = useState(false);
  const [isMapScriptLoading, setMapScriptLoading] = useState(false);
  const [, setMapReady] = useState(false);
  const [mapError, setMapError] = useState('');
  const [mapReloadToken, setMapReloadToken] = useState(0);
  const locationMapContainerRef = useRef(null);
  const locationMapInstanceRef = useRef(null);
  const locationMapMarkersRef = useRef([]);
  const locationMapInfoWindowRef = useRef(null);
  const fuelEventMapContainerRef = useRef(null);
  const fuelEventMapInstanceRef = useRef(null);
  const fuelEventMapMarkerRef = useRef(null);
  const fuelEventMapInfoWindowRef = useRef(null);
  const trackMapContainerRef = useRef(null);
  const trackMapInstanceRef = useRef(null);
  const trackMapMarkersRef = useRef([]);
  const trackMapPolylineRef = useRef(null);
  const trackMapInfoWindowRef = useRef(null);
  const trackMapCurrentMarkerRef = useRef(null);
  const trackPlaybackTimerRef = useRef(null);
  const mapScriptWatchdogRef = useRef(null);
  const mapConfigRequestRef = useRef(null);

  const fleetCounts = useMemo(() => ({
    total: vehicles.length,
    active: vehicles.filter((vehicle) => vehicle.status === 'active').length,
    idle: vehicles.filter((vehicle) => vehicle.status === 'idle').length,
    offline: vehicles.filter((vehicle) => vehicle.status === 'offline').length
  }), [vehicles]);

  const filteredVehicles = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    return vehicles.filter((vehicle) => {
      const matchesStatus = statusFilter === 'all' ? true : vehicle.status === statusFilter;
      const matchesQuery = query ? [vehicle.plate, vehicle.name].join(' ').toLowerCase().includes(query) : true;
      return matchesStatus && matchesQuery;
    });
  }, [vehicles, searchText, statusFilter]);

  const selectedVehicle = useMemo(
    () => vehicles.find((vehicle) => vehicle.id === selectedVehicleId) || null,
    [vehicles, selectedVehicleId]
  );

  const isLocationSelectionMode = activeReport === 'location';
  const isFuelReportActive = activeReport === 'fuel';
  const isTrackReportActive = activeReport === 'track';
  const isTrackFuelReportActive = activeReport === 'trackFuel';
  const isTrackVisualReportActive = isTrackReportActive || isTrackFuelReportActive;
  const shouldEnableGoogleMaps = isLocationSelectionMode || isTrackVisualReportActive || (isFuelReportActive && Boolean(selectedFuelEventMapRow));

  const unixDateRange = useMemo(() => ({
    from: toUnixSeconds(dateRange.fromDateTime),
    to: toUnixSeconds(dateRange.toDateTime)
  }), [dateRange.fromDateTime, dateRange.toDateTime]);

  const fuelDateRange = useMemo(() => ({
    from: Number.isFinite(unixDateRange.from) ? unixDateRange.from * 1000 : null,
    to: Number.isFinite(unixDateRange.to) ? unixDateRange.to * 1000 : null
  }), [unixDateRange.from, unixDateRange.to]);

  const isUnixDateRangeValid = useMemo(
    () => Number.isFinite(unixDateRange.from) && Number.isFinite(unixDateRange.to) && unixDateRange.from <= unixDateRange.to,
    [unixDateRange.from, unixDateRange.to]
  );

  const locationSelectedVehicles = useMemo(() => {
    const selectedMap = new Map();
    selectedVehicleIds.forEach((id) => {
      const matchedVehicle = vehicles.find((vehicle) => vehicle.id === id);
      if (matchedVehicle) {
        selectedMap.set(matchedVehicle.id, matchedVehicle);
      }
    });
    return Array.from(selectedMap.values());
  }, [selectedVehicleIds, vehicles]);

  const locationMapMarkers = useMemo(
    () => locationSelectedVehicles.map((vehicle) => {
      const coordinates = getVehicleCoordinates(vehicle);
      if (!coordinates) return null;
      return {
        id: vehicle.id,
        plate: vehicle.plate,
        placeName: vehicle.placeName,
        status: vehicle.status,
        speed: vehicle.speed,
        fuelLevel: vehicle.fuelLevel,
        latitude: coordinates.latitude,
        longitude: coordinates.longitude
      };
    }).filter(Boolean),
    [locationSelectedVehicles]
  );

  const activeTrackData = useMemo(
    () => (isTrackFuelReportActive ? trackFuelData : trackData),
    [isTrackFuelReportActive, trackData, trackFuelData]
  );
  const activeTrackLoading = isTrackFuelReportActive ? isTrackFuelLoading : isTrackLoading;
  const activeTrackError = isTrackFuelReportActive ? trackFuelError : trackError;

  const trackSummary = useMemo(
    () => getTrackSummary(activeTrackData, activeTrackData?.lastKnownPosition || null),
    [activeTrackData]
  );

  const trackIgnitionGroups = useMemo(
    () => getTrackIgnitionEventGroups(activeTrackData?.events || []),
    [activeTrackData]
  );

  const trackEventMarkers = useMemo(
    () => getTrackEventMarkers(activeTrackData?.points || []),
    [activeTrackData]
  );

  const sampledTrackMarkers = useMemo(
    () => getSampledTrackMarkers(
      activeTrackData?.points || [],
      isTrackVisualReportActive ? 24 : TRACK_CLICKABLE_MARKERS_LIMIT,
      new Set(trackEventMarkers.map(({ point }) => point.id))
    ),
    [activeTrackData, isTrackVisualReportActive, trackEventMarkers]
  );
  const clampedTrackPlaybackIndex = useMemo(() => {
    const totalTrackPoints = activeTrackData?.points?.length || 0;
    if (!totalTrackPoints) return 0;
    return Math.min(Math.max(trackPlaybackIndex, 0), totalTrackPoints - 1);
  }, [activeTrackData, trackPlaybackIndex]);
  const trackPlaybackPoint = useMemo(
    () => activeTrackData?.points?.[clampedTrackPlaybackIndex] || null,
    [activeTrackData, clampedTrackPlaybackIndex]
  );
  const trackPlaybackIntervalMs = useMemo(() => Math.max(
    TRACK_MAP_PLAYBACK_MIN_INTERVAL_MS,
    Math.round(TRACK_MAP_PLAYBACK_BASE_INTERVAL_MS / (trackPlaybackSpeed || 1))
  ), [trackPlaybackSpeed]);
  const trackFuelChartOptions = useMemo(() => {
    const fuelLevelSeries = trackFuelData?.fuelLevelSeries || null;
    const fallbackSmoothedSeries = (trackFuelData?.points || [])
      .filter((point) => Number.isFinite(point?.timestamp) && Number.isFinite(point?.fuelVolume))
      .map((point) => [point.timestamp, Number(point.fuelVolume.toFixed(1))]);
    const hasFuelLevelSmoothedSeries = Array.isArray(fuelLevelSeries?.smoothedSeries) && fuelLevelSeries.smoothedSeries.length;
    const smoothedFuelSeries = hasFuelLevelSmoothedSeries
      ? fuelLevelSeries.smoothedSeries
      : fallbackSmoothedSeries;
    const actualFuelSeries = Array.isArray(fuelLevelSeries?.actualSeries)
      ? fuelLevelSeries.actualSeries
      : [];

    if (!smoothedFuelSeries.length && !actualFuelSeries.length) return null;

    const combinedFuelValues = [...smoothedFuelSeries, ...actualFuelSeries]
      .map((point) => Number(point?.[1]))
      .filter((value) => Number.isFinite(value));
    const tankCapacity = Number(fuelLevelSeries?.tankCapacity);
    let yAxisMaxCandidate = null;
    if (Number.isFinite(tankCapacity)) yAxisMaxCandidate = tankCapacity;
    else if (combinedFuelValues.length) yAxisMaxCandidate = Math.max(...combinedFuelValues);
    const yAxisMax = Number.isFinite(yAxisMaxCandidate)
      ? Math.max(10, Math.ceil(yAxisMaxCandidate / 10) * 10)
      : null;

    return {
      animation: false,
      grid: {
        top: 58,
        left: 56,
        right: 34,
        bottom: 84
      },
      legend: {
        top: 14,
        icon: 'roundRect',
        itemWidth: 18,
        itemHeight: 10,
        textStyle: { color: '#475569', fontSize: 12, fontWeight: 700 }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'line',
          lineStyle: { color: 'rgba(148, 163, 184, 0.5)', width: 1 }
        },
        backgroundColor: 'rgba(15, 23, 42, 0.94)',
        borderWidth: 0,
        textStyle: { color: '#F8FAFC' },
        formatter: (items) => {
          const rows = Array.isArray(items) ? items : [items];
          const [firstItem] = rows;
          const timestamp = firstItem?.value?.[0];
          const lines = rows.map((item) => {
            const suffix = ' L';
            return `${item.marker}${item.seriesName}: ${Number(item.value?.[1] || 0).toLocaleString('en-IN', {
              maximumFractionDigits: 1,
              minimumFractionDigits: 1
            })}${suffix}`;
          });
          return [
            moment(timestamp).format('DD MMM YYYY, hh:mm A'),
            ...lines
          ].join('<br/>');
        }
      },
      xAxis: {
        type: 'time',
        axisLabel: {
          color: '#64748B',
          formatter: (value) => moment(value).format('DD MMM\nHH:mm')
        },
        axisLine: { lineStyle: { color: '#CBD5E1' } },
        splitLine: { show: false }
      },
      yAxis: {
        type: 'value',
        name: 'Fuel (L)',
        nameTextStyle: { color: '#475569', fontWeight: 700 },
        axisLabel: {
          color: '#64748B',
          formatter: (value) => `${value}`
        },
        max: yAxisMax || undefined,
        axisLine: { show: false },
        splitLine: { lineStyle: { color: 'rgba(148, 163, 184, 0.18)' } }
      },
      dataZoom: [
        {
          type: 'inside',
          throttle: 60
        },
        {
          type: 'slider',
          height: 26,
          bottom: 18,
          borderColor: 'rgba(148, 163, 184, 0.18)',
          fillerColor: 'rgba(37, 99, 235, 0.12)',
          handleStyle: { color: '#2563EB' }
        }
      ],
      series: [
        {
          name: 'Smooth fuel',
          type: 'line',
          showSymbol: false,
          smooth: 0.2,
          sampling: 'lttb',
          lineStyle: { width: 3.2, color: '#0F172A' },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(15, 23, 42, 0.16)' },
                { offset: 1, color: 'rgba(15, 23, 42, 0.02)' }
              ]
            }
          },
          emphasis: { focus: 'series' },
          data: smoothedFuelSeries
        },
        {
          name: 'Actual fuel',
          type: 'line',
          showSymbol: false,
          smooth: false,
          sampling: 'lttb',
          lineStyle: { width: 1.8, color: '#F97316', opacity: 0.92 },
          itemStyle: { color: '#F97316' },
          emphasis: { focus: 'series' },
          data: actualFuelSeries
        }
      ]
    };
  }, [trackFuelData]);

  const resolvedGoogleMapsMapId = useMemo(() => getResolvedGoogleMapId(googleMapsMapId), [googleMapsMapId]);
  const refreshStatusLabel = useMemo(
    () => formatRefreshStatusLabel(lastFleetRefreshAt, refreshStatusTick),
    [lastFleetRefreshAt, refreshStatusTick]
  );

  const loadGoogleMapsConfig = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh) {
      if (googleMapsKey) {
        setMapError('');
        setMapScriptLoading(!isGoogleMapsApiReady());
        return { apiKey: googleMapsKey, mapId: googleMapsMapId };
      }
    }

    if (!forceRefresh && mapConfigRequestRef.current) {
      return mapConfigRequestRef.current;
    }

    setMapKeyLoading(true);
    setMapScriptLoading(false);
    setMapReady(false);
    setMapError('');

    const requestPromise = (async () => {
      try {
        const response = await APIService.getFleetGoogleMapsApiKey();
        const apiKeyCandidates = [
          response?.result?.google_maps_api_key,
          response?.result?.googleMapApiKey,
          response?.result?.api_key,
          response?.result?.map_api_key,
          response?.result?.data?.google_maps_api_key,
          response?.result?.data?.googleMapApiKey,
          response?.result?.data?.api_key,
          response?.google_maps_api_key,
          response?.api_key
        ];
        const apiKey = apiKeyCandidates.find((value) => typeof value === 'string' && value.trim())?.trim();
        const mapIdCandidates = [
          response?.result?.google_maps_map_id,
          response?.result?.google_map_id,
          response?.result?.map_id,
          response?.result?.googleMapId,
          response?.result?.mapId,
          response?.result?.data?.google_maps_map_id,
          response?.result?.data?.google_map_id,
          response?.result?.data?.map_id,
          response?.result?.data?.googleMapId,
          response?.result?.data?.mapId,
          response?.google_maps_map_id,
          response?.google_map_id,
          response?.map_id,
          response?.mapId
        ];
        const apiMapId = mapIdCandidates.find((value) => typeof value === 'string' && value.trim())?.trim() || '';

        if (!apiKey) throw new Error('Google Maps API key missing in response');

        try {
          window.sessionStorage.setItem(MAP_KEY_SESSION_STORAGE_KEY, apiKey);
          if (apiMapId) window.sessionStorage.setItem(MAP_ID_SESSION_STORAGE_KEY, apiMapId);
          else window.sessionStorage.removeItem(MAP_ID_SESSION_STORAGE_KEY);
        } catch (error) {
          console.log('google maps key cache write error', error?.message || error);
        }

        setGoogleMapsKey(apiKey);
        setGoogleMapsMapId(apiMapId);
        setMapScriptLoading(true);
        return { apiKey, mapId: apiMapId };
      } catch (error) {
        console.log('google maps key fetch error', error?.message || error);
        setMapError('Unable to load map. Please retry.');
        setGoogleMapsKey('');
        setGoogleMapsMapId('');
        setMapScriptLoading(false);
        return null;
      } finally {
        setMapKeyLoading(false);
        mapConfigRequestRef.current = null;
      }
    })();

    mapConfigRequestRef.current = requestPromise;
    return requestPromise;
  }, [googleMapsKey, googleMapsMapId]);

  const handleVehicleSelect = (vehicleId) => {
    if (!isLocationSelectionMode) {
      setSelectedVehicleId(vehicleId);
      setSelectedVehicleIds((previousIds) => {
        if (previousIds.length === 1 && previousIds[0] === vehicleId) return previousIds;
        return [vehicleId];
      });
      loadGoogleMapsConfig();
      return;
    }

    if (selectedVehicleIds.includes(vehicleId)) {
      const nextIds = selectedVehicleIds.filter((id) => id !== vehicleId);
      setSelectedVehicleIds(nextIds);
      if (selectedVehicleId === vehicleId) {
        setSelectedVehicleId(nextIds[0] || '');
      }
      return;
    }

    setSelectedVehicleId(vehicleId);
    setSelectedVehicleIds([...selectedVehicleIds, vehicleId]);
    loadGoogleMapsConfig();
  };

  const handleReportChange = useCallback((_, value) => {
    setActiveReport(value);
    if ((value === 'track' || value === 'trackFuel' || value === 'location') && selectedVehicleId) {
      loadGoogleMapsConfig();
    }
  }, [loadGoogleMapsConfig, selectedVehicleId]);

  const handleDatePresetChange = (presetValue) => {
    setDateRangePreset(presetValue);
    setDateRange(getDateTimeRangeForPreset(presetValue));
  };

  const handleDateRangeChange = (field, value) => {
    setDateRangePreset('custom');
    setDateRange((previousRange) => {
      const nextRange = { ...previousRange, [field]: value };
      const nextFrom = moment(nextRange.fromDateTime, DATE_TIME_INPUT_FORMAT, true);
      const nextTo = moment(nextRange.toDateTime, DATE_TIME_INPUT_FORMAT, true);

      if (
        field === 'fromDateTime'
        && nextFrom.isValid()
        && nextTo.isValid()
        && nextFrom.isAfter(nextTo)
      ) {
        nextRange.toDateTime = value;
      }

      if (
        field === 'toDateTime'
        && nextFrom.isValid()
        && nextTo.isValid()
        && nextTo.isBefore(nextFrom)
      ) {
        nextRange.fromDateTime = value;
      }

      return nextRange;
    });
  };

  const handleRetryLocationMap = () => {
    if (mapScriptWatchdogRef.current) {
      clearTimeout(mapScriptWatchdogRef.current);
      mapScriptWatchdogRef.current = null;
    }
    setMapError('');
    setMapReady(false);
    setGoogleMapsKey('');
    setGoogleMapsMapId('');
    locationMapInstanceRef.current = null;
    const existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
    existingScript?.remove();
    setMapReloadToken((previousToken) => previousToken + 1);
  };

  const clearLocationMapMarkers = () => {
    if (locationMapInfoWindowRef.current) {
      locationMapInfoWindowRef.current.close();
      locationMapInfoWindowRef.current = null;
    }
    locationMapMarkersRef.current.forEach((marker) => {
      if (typeof marker?.setMap === 'function') {
        marker.setMap(null);
      } else if (marker && 'map' in marker) {
        const currentMarker = marker;
        currentMarker.map = null;
      }
    });
    locationMapMarkersRef.current = [];
  };

  const clearTrackMapArtifacts = () => {
    if (trackMapInfoWindowRef.current) {
      trackMapInfoWindowRef.current.close();
      trackMapInfoWindowRef.current = null;
    }

    if (trackMapPolylineRef.current) {
      trackMapPolylineRef.current.setMap(null);
      trackMapPolylineRef.current = null;
    }

    trackMapMarkersRef.current.forEach((marker) => {
      if (typeof marker?.setMap === 'function') marker.setMap(null);
    });
    trackMapMarkersRef.current = [];
    trackMapCurrentMarkerRef.current = null;
  };

  const clearFuelEventMapArtifacts = () => {
    if (fuelEventMapInfoWindowRef.current) {
      fuelEventMapInfoWindowRef.current.close();
      fuelEventMapInfoWindowRef.current = null;
    }

    if (fuelEventMapMarkerRef.current && typeof fuelEventMapMarkerRef.current.setMap === 'function') {
      fuelEventMapMarkerRef.current.setMap(null);
    }
    fuelEventMapMarkerRef.current = null;
  };

  const getLocationMarkerIcon = () => {
    if (!isGoogleMapsApiReady()) return undefined;
    const markerSvg = buildVehicleMarkerIconSvg();
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(markerSvg)}`,
      scaledSize: new window.google.maps.Size(
        LOCATION_MARKER_ICON_DIMENSION.width,
        LOCATION_MARKER_ICON_DIMENSION.height
      ),
      anchor: new window.google.maps.Point(
        Math.floor(LOCATION_MARKER_ICON_DIMENSION.width / 2),
        LOCATION_MARKER_ICON_DIMENSION.height - 1
      )
    };
  };

  const getTrackCircleMarkerIcon = (
    fillColor,
    scale = 7,
    strokeColor = '#FFFFFF',
    strokeWeight = 2,
    fillOpacity = 1
  ) => {
    if (!isGoogleMapsApiReady()) return undefined;
    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor,
      fillOpacity,
      strokeColor,
      strokeWeight,
      scale
    };
  };

  const getTrackCurrentMarkerIcon = () => getLocationMarkerIcon();

  const createLocationMarker = (mapInstance, markerData) => {
    const markerPosition = { lat: markerData.latitude, lng: markerData.longitude };
    return new window.google.maps.Marker({
      map: mapInstance,
      position: markerPosition,
      title: markerData.plate,
      icon: getLocationMarkerIcon(),
      optimized: true
    });
  };

  const attachMarkerClickHandler = (markerInstance, onClick) => {
    if (typeof markerInstance?.addListener === 'function') {
      markerInstance.addListener('click', onClick);
      return;
    }
    if (typeof markerInstance?.addEventListener === 'function') {
      markerInstance.addEventListener('click', onClick);
    }
  };

  useEffect(() => {
    if (!shouldEnableGoogleMaps) {
      clearLocationMapMarkers();
      clearTrackMapArtifacts();
      clearFuelEventMapArtifacts();
      setMapKeyLoading(false);
      setMapScriptLoading(false);
      setMapReady(false);
      setMapError('');
      setFuelEventMapError('');
      locationMapInstanceRef.current = null;
      fuelEventMapInstanceRef.current = null;
      trackMapInstanceRef.current = null;
      return;
    }

    loadGoogleMapsConfig(Boolean(mapReloadToken));
  }, [shouldEnableGoogleMaps, mapReloadToken, loadGoogleMapsConfig]);

  useEffect(() => {
    if (!shouldEnableGoogleMaps || !googleMapsKey) return undefined;
    let isMounted = true;
    let readinessInterval = null;
    const previousAuthFailureHandler = window.gm_authFailure;

    const handleScriptReady = () => {
      if (!isMounted || !isGoogleMapsApiReady()) return;
      const existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
      existingScript?.setAttribute('data-loaded', 'true');
      if (readinessInterval) {
        clearInterval(readinessInterval);
        readinessInterval = null;
      }
      if (mapScriptWatchdogRef.current) {
        clearTimeout(mapScriptWatchdogRef.current);
        mapScriptWatchdogRef.current = null;
      }
      setMapScriptLoading(false);
      setMapError('');
    };

    const handleScriptError = (message = 'Unable to load map. Please retry.') => {
      if (!isMounted) return;
      if (readinessInterval) {
        clearInterval(readinessInterval);
        readinessInterval = null;
      }
      if (mapScriptWatchdogRef.current) {
        clearTimeout(mapScriptWatchdogRef.current);
        mapScriptWatchdogRef.current = null;
      }
      setMapScriptLoading(false);
      setMapReady(false);
      setMapError(message);
    };

    if (isGoogleMapsApiReady()) {
      handleScriptReady();
      return () => { isMounted = false; };
    }

    setMapScriptLoading(true);
    const callbackHandler = () => handleScriptReady();
    const scriptLoadHandler = () => handleScriptReady();
    window[GOOGLE_MAPS_INIT_CALLBACK] = callbackHandler;
    window.gm_authFailure = () => {
      handleScriptError('Google Maps API key is not authorized for this domain.');
      if (typeof previousAuthFailureHandler === 'function') previousAuthFailureHandler();
    };

    const expectedScriptSrc = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(googleMapsKey)}&v=weekly&libraries=marker&loading=async&callback=${GOOGLE_MAPS_INIT_CALLBACK}`;
    let scriptElement = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
    if (scriptElement) {
      const existingSrc = scriptElement.getAttribute('src') || '';
      if (existingSrc !== expectedScriptSrc && !isGoogleMapsApiReady()) {
        scriptElement.remove();
        scriptElement = null;
      }
    }

    if (!scriptElement) {
      scriptElement = document.createElement('script');
      scriptElement.id = GOOGLE_MAPS_SCRIPT_ID;
      scriptElement.src = expectedScriptSrc;
      scriptElement.async = true;
      scriptElement.defer = true;
      document.head.appendChild(scriptElement);
    } else if (scriptElement.getAttribute('data-loaded') === 'true' && isGoogleMapsApiReady()) {
      handleScriptReady();
      return () => {
        isMounted = false;
        if (window[GOOGLE_MAPS_INIT_CALLBACK] === callbackHandler) delete window[GOOGLE_MAPS_INIT_CALLBACK];
      };
    }

    readinessInterval = setInterval(() => {
      if (!isMounted) return;
      if (isGoogleMapsApiReady()) handleScriptReady();
    }, 150);

    mapScriptWatchdogRef.current = setTimeout(() => {
      if (!isMounted) return;
      if (!isGoogleMapsApiReady()) handleScriptError();
      else handleScriptReady();
    }, 15000);

    scriptElement.addEventListener('load', scriptLoadHandler);
    scriptElement.addEventListener('error', handleScriptError);

    return () => {
      isMounted = false;
      if (readinessInterval) {
        clearInterval(readinessInterval);
        readinessInterval = null;
      }
      if (mapScriptWatchdogRef.current) {
        clearTimeout(mapScriptWatchdogRef.current);
        mapScriptWatchdogRef.current = null;
      }
      scriptElement?.removeEventListener('load', scriptLoadHandler);
      scriptElement?.removeEventListener('error', handleScriptError);
      if (window[GOOGLE_MAPS_INIT_CALLBACK] === callbackHandler) delete window[GOOGLE_MAPS_INIT_CALLBACK];
      window.gm_authFailure = previousAuthFailureHandler;
    };
  }, [googleMapsKey, shouldEnableGoogleMaps]);

  useEffect(() => {
    if (!isLocationSelectionMode || isMapScriptLoading || !isGoogleMapsApiReady()) {
      return undefined;
    }

    const mapContainer = locationMapContainerRef.current;
    if (!mapContainer) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      try {
        if (!mapContainer.isConnected || locationMapContainerRef.current !== mapContainer) {
          return;
        }

        let mapInstance = locationMapInstanceRef.current;
        const shouldCreateMap = !mapInstance
          || mapInstance.getDiv() !== mapContainer
          || mapInstance.fleetManagementMapId !== resolvedGoogleMapsMapId;

        // FIX: Cost Optimization - Only execute new google map instances when actually required
        if (shouldCreateMap) {
          const initialCenter = locationMapMarkers[0]
            ? { lat: locationMapMarkers[0].latitude, lng: locationMapMarkers[0].longitude }
            : DEFAULT_LOCATION_MAP_CENTER;
          const mapOptions = { ...LOCATION_MAP_OPTIONS, center: initialCenter, zoom: 12 };
          if (resolvedGoogleMapsMapId) mapOptions.mapId = resolvedGoogleMapsMapId;

          mapInstance = new window.google.maps.Map(mapContainer, mapOptions);
          mapInstance.fleetManagementMapId = resolvedGoogleMapsMapId;
          locationMapInstanceRef.current = mapInstance;
        }

        clearLocationMapMarkers();

        // FIX: Cost Optimization - Do NOT drop Map instance when no markers. Just return out.
        if (!locationMapMarkers.length) {
          setMapReady(true);
          return;
        }

        locationMapMarkersRef.current = locationMapMarkers.map((marker) => {
          const markerInstance = createLocationMarker(mapInstance, marker);
          attachMarkerClickHandler(markerInstance, () => {
            mapInstance.setCenter({ lat: marker.latitude, lng: marker.longitude });
            mapInstance.setZoom(Math.max(mapInstance.getZoom() || 12, 16));
            if (!locationMapInfoWindowRef.current) locationMapInfoWindowRef.current = new window.google.maps.InfoWindow();
            locationMapInfoWindowRef.current.setContent(getMapMarkerInfoContent(marker));
            locationMapInfoWindowRef.current.open({ anchor: markerInstance, map: mapInstance, shouldFocus: false });
          });
          return markerInstance;
        });

        fitLocationMapToMarkers(mapInstance, locationMapMarkers);

        if (locationMapMarkers.length === 1) {
          const onlyMarker = locationMapMarkers[0];
          const markerInstance = locationMapMarkersRef.current[0];
          if (!locationMapInfoWindowRef.current) locationMapInfoWindowRef.current = new window.google.maps.InfoWindow();
          locationMapInfoWindowRef.current.setContent(getMapMarkerInfoContent(onlyMarker));
          locationMapInfoWindowRef.current.open({ anchor: markerInstance, map: mapInstance, shouldFocus: false });
        } else if (locationMapInfoWindowRef.current) {
          locationMapInfoWindowRef.current.close();
        }

        setMapError('');
        setMapReady(true);
      } catch (error) {
        console.log('location map render error', error?.message || error);
        setMapReady(false);
        setMapError('Unable to render map. Please retry.');
      }
    });

    return () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);
    };
  }, [isLocationSelectionMode, isMapScriptLoading, locationMapMarkers, resolvedGoogleMapsMapId]);

  useEffect(() => {
    if (!isTrackVisualReportActive || isMapScriptLoading || !isGoogleMapsApiReady()) {
      return undefined;
    }

    const mapContainer = trackMapContainerRef.current;
    if (!mapContainer) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      try {
        if (!mapContainer.isConnected || trackMapContainerRef.current !== mapContainer) {
          return;
        }

        const routePoints = activeTrackData?.points || [];
        const { currentPoint } = trackSummary;
        const [firstRoutePoint] = routePoints;
        const displayPoint = currentPoint || firstRoutePoint;
        const initialCenterPoint = displayPoint || firstRoutePoint;

        let mapInstance = trackMapInstanceRef.current;
        const shouldCreateMap = !mapInstance
          || mapInstance.getDiv() !== mapContainer
          || mapInstance.fleetManagementMapId !== resolvedGoogleMapsMapId;

        if (shouldCreateMap) {
          const mapOptions = {
            ...LOCATION_MAP_OPTIONS,
            center: initialCenterPoint
              ? { lat: initialCenterPoint.latitude, lng: initialCenterPoint.longitude }
              : DEFAULT_LOCATION_MAP_CENTER,
            zoom: 12
          };
          if (resolvedGoogleMapsMapId) mapOptions.mapId = resolvedGoogleMapsMapId;

          mapInstance = new window.google.maps.Map(mapContainer, mapOptions);
          mapInstance.fleetManagementMapId = resolvedGoogleMapsMapId;
          trackMapInstanceRef.current = mapInstance;
          window.google.maps.event.trigger(mapInstance, 'resize');
        }

        clearTrackMapArtifacts();
        window.google.maps.event.trigger(mapInstance, 'resize');

        const fitPoints = [];

        if (routePoints.length > 1) {
          const polylineOptions = {
            path: routePoints.map((point) => ({ lat: point.latitude, lng: point.longitude })),
            geodesic: true,
            strokeColor: '#2563EB',
            strokeOpacity: 0.92,
            strokeWeight: 4
          };

          if (window.google.maps.SymbolPath?.FORWARD_CLOSED_ARROW && routePoints.length > 2) {
            polylineOptions.icons = [{
              icon: {
                path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                fillColor: '#1D4ED8',
                fillOpacity: 1,
                strokeColor: '#DBEAFE',
                strokeWeight: 1,
                scale: 2.8
              },
              offset: '0',
              repeat: '48px'
            }];
          }

          trackMapPolylineRef.current = new window.google.maps.Polyline(polylineOptions);
          trackMapPolylineRef.current.setMap(mapInstance);
          fitPoints.push(...routePoints);
        }

        const createTrackMarker = ({
          point,
          title,
          icon,
          label,
          zIndex = 1,
          pointIndex = null,
          markerType = 'point'
        }) => {
          if (!point || !Number.isFinite(point.latitude) || !Number.isFinite(point.longitude)) return null;
          const markerInstance = new window.google.maps.Marker({
            map: mapInstance,
            position: { lat: point.latitude, lng: point.longitude },
            title,
            icon,
            label,
            zIndex,
            optimized: true
          });
          attachMarkerClickHandler(markerInstance, () => {
            setSelectedTrackPoint({
              point,
              pointIndex,
              title,
              markerType
            });
            if (!isTrackVisualReportActive) {
              if (!trackMapInfoWindowRef.current) trackMapInfoWindowRef.current = new window.google.maps.InfoWindow();
              trackMapInfoWindowRef.current.setContent(getTrackMarkerInfoContent({
                title,
                plate: selectedVehicle?.plate || 'Vehicle',
                speed: point.speed,
                fuelVolume: point.fuelVolume,
                satellitesCount: point.satellitesCount,
                timestamp: point.timestamp || point.eventDate || point.rawTimestamp,
                direction: point.direction,
                latitude: point.latitude,
                longitude: point.longitude,
                ignition: point.ignition,
                isStopped: point.isStopped,
                gpsValid: point.gpsValid,
                events: point.events || [],
                isLoading: !isTrackFuelReportActive
              }));
              trackMapInfoWindowRef.current.open({ anchor: markerInstance, map: mapInstance, shouldFocus: false });
            }
          });
          trackMapMarkersRef.current.push(markerInstance);
          fitPoints.push(point);
          return markerInstance;
        };

        const startPoint = routePoints[0];
        const endPoint = routePoints[routePoints.length - 1];
        const shouldShowSeparateEndMarker = Boolean(
          endPoint
          && (
            !currentPoint
            || currentPoint.latitude !== endPoint.latitude
            || currentPoint.longitude !== endPoint.longitude
          )
        );

        createTrackMarker({
          point: startPoint,
          title: 'Trip start',
          icon: getTrackCircleMarkerIcon('#16A34A'),
          pointIndex: 0,
          markerType: 'start'
        });

        sampledTrackMarkers.forEach(({ point, pointIndex }) => {
          createTrackMarker({
            point,
            pointIndex,
            markerType: 'route',
            title: `Track point ${pointIndex + 1}`,
            icon: getTrackCircleMarkerIcon('#0F172A', 6, '#E2E8F0', 2, 0.9),
            zIndex: 2
          });
        });

        if (!isTrackVisualReportActive) {
          trackEventMarkers.forEach(({ point, pointIndex }) => {
            const dominantEvent = point.events?.[0] || null;
            const markerTitle = point.eventCount > 1
              ? `${point.eventCount} events`
              : dominantEvent?.shortLabel || dominantEvent?.label || `Event point ${pointIndex + 1}`;

            createTrackMarker({
              point,
              pointIndex,
              markerType: 'event',
              title: markerTitle,
              icon: getTrackCircleMarkerIcon(dominantEvent?.color || '#F97316', 11, '#FFFFFF', 2.5, 0.96),
              label: {
                text: `${point.eventCount}`,
                color: '#FFFFFF',
                fontWeight: '700',
                fontSize: '10px'
              },
              zIndex: 6
            });
          });
        }

        if (isTrackVisualReportActive) {
          trackIgnitionGroups.forEach((group) => {
            const markerInstance = new window.google.maps.Marker({
              map: mapInstance,
              position: { lat: group.latitude, lng: group.longitude },
              title: group.title,
              icon: getTrackCircleMarkerIcon('#F97316', 13, '#FFFFFF', 2.5, 0.96),
              label: {
                text: `${group.eventCount}`,
                color: '#FFFFFF',
                fontWeight: '700',
                fontSize: '11px'
              },
              zIndex: 7,
              optimized: true
            });
            attachMarkerClickHandler(markerInstance, () => {
              setSelectedTrackEventGroup(group);
            });
            trackMapMarkersRef.current.push(markerInstance);
            fitPoints.push(group);
          });
        }

        if (shouldShowSeparateEndMarker) {
          createTrackMarker({
            point: endPoint,
            title: 'Trip end',
            icon: getTrackCircleMarkerIcon('#DC2626'),
            pointIndex: routePoints.length - 1,
            markerType: 'end'
          });
        }

        const currentMarker = createTrackMarker({
          point: displayPoint || currentPoint,
          title: isTrackVisualReportActive ? 'Playback position' : 'Last known position',
          icon: getTrackCurrentMarkerIcon(),
          zIndex: 10,
          pointIndex: routePoints.length ? routePoints.length - 1 : null,
          markerType: 'current'
        });
        trackMapCurrentMarkerRef.current = currentMarker;

        if (fitPoints.length) {
          fitMapToCoordinatePoints(mapInstance, fitPoints);
        } else {
          mapInstance.setCenter(DEFAULT_LOCATION_MAP_CENTER);
          mapInstance.setZoom(11);
        }

        if (currentMarker) {
          if (!trackMapInfoWindowRef.current) trackMapInfoWindowRef.current = new window.google.maps.InfoWindow();
          trackMapInfoWindowRef.current.setContent(getTrackMarkerInfoContent({
            title: isTrackVisualReportActive ? 'Playback position' : 'Last known position',
            plate: selectedVehicle?.plate || 'Vehicle',
            speed: displayPoint?.speed,
            fuelVolume: displayPoint?.fuelVolume,
            satellitesCount: displayPoint?.satellitesCount,
            timestamp: displayPoint?.timestamp || displayPoint?.eventDate || displayPoint?.rawTimestamp,
            direction: displayPoint?.direction,
            latitude: displayPoint?.latitude,
            longitude: displayPoint?.longitude,
            ignition: displayPoint?.ignition,
            isStopped: displayPoint?.isStopped,
            gpsValid: displayPoint?.gpsValid ?? displayPoint?.gpsDataValid ?? null,
            events: displayPoint?.events || [],
            isLoading: !isTrackFuelReportActive
          }));
          if (!isTrackVisualReportActive) {
            trackMapInfoWindowRef.current.open({ anchor: currentMarker, map: mapInstance, shouldFocus: false });
          }
        }

        setMapError('');
        setMapReady(true);
      } catch (error) {
        console.log('track map render error', error?.message || error);
        setMapReady(false);
        setMapError('Unable to render map. Please retry.');
      }
    });

    return () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);
    };
  }, [activeTrackData, isMapScriptLoading, isTrackFuelReportActive, isTrackVisualReportActive, resolvedGoogleMapsMapId, sampledTrackMarkers, trackEventMarkers, trackIgnitionGroups, trackSummary.currentPoint, selectedVehicle?.plate]);

  useEffect(() => {
    if (!isFuelReportActive || !selectedFuelEventMapRow || isMapScriptLoading || !isGoogleMapsApiReady()) {
      return undefined;
    }

    const mapContainer = fuelEventMapContainerRef.current;
    if (!mapContainer) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      try {
        if (!mapContainer.isConnected || fuelEventMapContainerRef.current !== mapContainer) {
          return;
        }

        const markerLatitude = Number(selectedFuelEventMapRow.latitude);
        const markerLongitude = Number(selectedFuelEventMapRow.longitude);
        if (!Number.isFinite(markerLatitude) || !Number.isFinite(markerLongitude)) {
          clearFuelEventMapArtifacts();
          setFuelEventMapError('Coordinates are unavailable for this event.');
          return;
        }

        let mapInstance = fuelEventMapInstanceRef.current;
        const shouldCreateMap = !mapInstance
          || mapInstance.getDiv() !== mapContainer
          || mapInstance.fleetManagementMapId !== resolvedGoogleMapsMapId;

        if (shouldCreateMap) {
          const mapOptions = {
            ...LOCATION_MAP_OPTIONS,
            center: { lat: markerLatitude, lng: markerLongitude },
            zoom: 15
          };
          if (resolvedGoogleMapsMapId) mapOptions.mapId = resolvedGoogleMapsMapId;

          mapInstance = new window.google.maps.Map(mapContainer, mapOptions);
          mapInstance.fleetManagementMapId = resolvedGoogleMapsMapId;
          fuelEventMapInstanceRef.current = mapInstance;
        }

        clearFuelEventMapArtifacts();

        let markerColor = '#2563EB';
        if (selectedFuelEventMapRow.type === 1) markerColor = '#16A34A';
        else if (selectedFuelEventMapRow.type === 3) markerColor = '#DC2626';

        const markerInstance = new window.google.maps.Marker({
          map: mapInstance,
          position: { lat: markerLatitude, lng: markerLongitude },
          title: selectedFuelEventMapRow.typeLabel || 'Fuel event',
          icon: getTrackCircleMarkerIcon(markerColor, 10, '#FFFFFF', 2.5, 0.95),
          optimized: true
        });
        fuelEventMapMarkerRef.current = markerInstance;

        if (!fuelEventMapInfoWindowRef.current) fuelEventMapInfoWindowRef.current = new window.google.maps.InfoWindow();
        fuelEventMapInfoWindowRef.current.setContent(getFuelEventMarkerInfoContent(selectedFuelEventMapRow));
        fuelEventMapInfoWindowRef.current.open({ anchor: markerInstance, map: mapInstance, shouldFocus: false });

        mapInstance.setCenter({ lat: markerLatitude, lng: markerLongitude });
        mapInstance.setZoom(Math.max(mapInstance.getZoom() || 15, 15));

        setFuelEventMapError('');
        setMapError('');
        setMapReady(true);
      } catch (error) {
        console.log('fuel event map render error', error?.message || error);
        setMapReady(false);
        setFuelEventMapError('Unable to render event map. Please retry.');
      }
    });

    return () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);
    };
  }, [isFuelReportActive, isMapScriptLoading, resolvedGoogleMapsMapId, selectedFuelEventMapRow]);

  useEffect(() => {
    if (!isTrackVisualReportActive || !activeTrackData) {
      setSelectedTrackPoint(null);
      return;
    }

    if (isTrackVisualReportActive) {
      setSelectedTrackPoint(null);
      return;
    }

    const fallbackPoint = trackSummary.currentPoint || trackSummary.endPoint || trackSummary.startPoint;
    if (!fallbackPoint) {
      setSelectedTrackPoint(null);
      return;
    }

    setSelectedTrackPoint({
      point: fallbackPoint,
      pointIndex: activeTrackData?.points?.length ? activeTrackData.points.length - 1 : null,
      title: trackSummary.currentPoint ? 'Last known position' : 'Track point',
      markerType: trackSummary.currentPoint ? 'current' : 'route'
    });
  }, [activeTrackData, isTrackVisualReportActive, trackSummary.currentPoint, trackSummary.endPoint, trackSummary.startPoint]);

  useEffect(() => {
    if (isFuelReportActive) return;
    setSelectedFuelEventMapRow(null);
    setFuelEventMapError('');
    clearFuelEventMapArtifacts();
  }, [isFuelReportActive]);

  useEffect(() => {
    if (!isTrackVisualReportActive) {
      setSelectedTrackEventGroup(null);
      setTrackPlaybackIndex(0);
      setTrackPlaybackActive(false);
      return;
    }

    const totalTrackPoints = activeTrackData?.points?.length || 0;
    if (!totalTrackPoints) {
      setSelectedTrackEventGroup(null);
      setTrackPlaybackIndex(0);
      setTrackPlaybackActive(false);
      return;
    }

    setTrackPlaybackIndex(0);
    setTrackPlaybackActive(false);
    setSelectedTrackEventGroup((previousGroup) => {
      if (previousGroup) {
        const matchedGroup = trackIgnitionGroups.find((group) => group.id === previousGroup.id);
        if (matchedGroup) return matchedGroup;
      }
      return null;
    });
  }, [activeTrackData, isTrackVisualReportActive, trackIgnitionGroups]);

  useEffect(() => {
    if (!isTrackVisualReportActive || !activeTrackData?.points?.length || !isTrackPlaybackActive) {
      if (trackPlaybackTimerRef.current) {
        window.clearInterval(trackPlaybackTimerRef.current);
        trackPlaybackTimerRef.current = null;
      }
      return undefined;
    }

    trackPlaybackTimerRef.current = window.setInterval(() => {
      setTrackPlaybackIndex((previousIndex) => {
        const lastPointIndex = Math.max((activeTrackData?.points?.length || 1) - 1, 0);
        if (previousIndex >= lastPointIndex) {
          setTrackPlaybackActive(false);
          return lastPointIndex;
        }
        return previousIndex + 1;
      });
    }, trackPlaybackIntervalMs);

    return () => {
      if (trackPlaybackTimerRef.current) {
        window.clearInterval(trackPlaybackTimerRef.current);
        trackPlaybackTimerRef.current = null;
      }
    };
  }, [activeTrackData, isTrackPlaybackActive, isTrackVisualReportActive, trackPlaybackIntervalMs]);

  useEffect(() => {
    if (!isTrackVisualReportActive || !trackPlaybackPoint || !isGoogleMapsApiReady()) return;

    const currentMarker = trackMapCurrentMarkerRef.current;
    if (currentMarker && typeof currentMarker.setPosition === 'function') {
      currentMarker.setPosition({
        lat: trackPlaybackPoint.latitude,
        lng: trackPlaybackPoint.longitude
      });
      currentMarker.setTitle(isTrackPlaybackActive ? 'Playback position' : 'Route position');
    }

    if (isTrackPlaybackActive && trackMapInstanceRef.current) {
      trackMapInstanceRef.current.panTo({
        lat: trackPlaybackPoint.latitude,
        lng: trackPlaybackPoint.longitude
      });
    }
  }, [isTrackPlaybackActive, isTrackVisualReportActive, trackPlaybackPoint]);

  useEffect(() => {
    const detailsPoint = selectedTrackPoint?.point || null;

    if (!isTrackVisualReportActive || !selectedVehicle?.plate || !detailsPoint) {
      setSelectedTrackPointDetails(null);
      setSelectedTrackPointDetailsError('');
      setSelectedTrackPointDetailsLoading(false);
      return undefined;
    }

    const eventDate = detailsPoint.timestamp
      ?? getNormalizedTimestampValue(detailsPoint.rawTimestamp);

    if (!Number.isFinite(eventDate)) {
      setSelectedTrackPointDetails(null);
      setSelectedTrackPointDetailsError('Point details are unavailable for this marker.');
      setSelectedTrackPointDetailsLoading(false);
      return undefined;
    }

    let isMounted = true;
    const payload = {
      registration_num: selectedVehicle.plate,
      eventDate
    };

    const fetchTrackPointDetails = async () => {
      setSelectedTrackPointDetailsLoading(true);
      setSelectedTrackPointDetailsError('');
      try {
        const response = await APIService.getFleetLocationMapCard(payload);
        if (!isMounted) return;

        const normalizedDetails = normalizeLocationMapCardData(response);
        if (normalizedDetails) {
          setSelectedTrackPointDetails(normalizedDetails);
        } else {
          setSelectedTrackPointDetails(null);
          setSelectedTrackPointDetailsError('No point details were returned for this timestamp.');
        }
      } catch (error) {
        if (!isMounted) return;
        console.log('fleet track point details error', error?.message || error);
        setSelectedTrackPointDetails(null);
        setSelectedTrackPointDetailsError('Unable to load point details right now. Please try another marker.');
      } finally {
        if (isMounted) setSelectedTrackPointDetailsLoading(false);
      }
    };

    fetchTrackPointDetails();
    return () => { isMounted = false; };
  }, [isTrackVisualReportActive, selectedTrackPoint, selectedVehicle?.plate]);

  const fetchCurrentState = useCallback(async (showInitialLoading = false) => {
    if (showInitialLoading) setFleetLoading(true);
    else setRefreshingFleet(true);

    try {
      const response = await APIService.getFleetCurrentState();
      const currentStateRows = response?.result?.data;
      const parsedVehicles = buildCurrentStateVehicles(currentStateRows);
      setVehicles(parsedVehicles);
      setLastFleetRefreshAt(Date.now());
    } catch (error) {
      console.log('fleet current state error', error?.message || error);
    } finally {
      if (showInitialLoading) setFleetLoading(false);
      else setRefreshingFleet(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrentState(true);
  }, [fetchCurrentState]);

  useEffect(() => {
    const refreshStatusTimer = setInterval(() => setRefreshStatusTick(Date.now()), 15000);
    return () => clearInterval(refreshStatusTimer);
  }, []);

  useEffect(() => {
    setSelectedVehicleIds((previousIds) => previousIds.filter((id) => vehicles.some((vehicle) => vehicle.id === id)));
  }, [vehicles]);

  useEffect(() => {
    if (isLocationSelectionMode) return;
    if (!selectedVehicleId) {
      setSelectedVehicleIds((previousIds) => (previousIds.length ? [] : previousIds));
      return;
    }
    setSelectedVehicleIds((previousIds) => (
      previousIds.length === 1 && previousIds[0] === selectedVehicleId ? previousIds : [selectedVehicleId]
    ));
  }, [isLocationSelectionMode, selectedVehicleId]);

  useEffect(() => {
    if (selectedVehicleId && !vehicles.some((vehicle) => vehicle.id === selectedVehicleId)) setSelectedVehicleId('');
  }, [vehicles, selectedVehicleId]);

  useEffect(() => {
    if (!selectedVehicleId) return;
    loadGoogleMapsConfig();
  }, [selectedVehicleId, loadGoogleMapsConfig]);

  useEffect(() => {
    if (isLocationSelectionMode) {
      setVehicleLoading(false);
      return undefined;
    }
    if (!selectedVehicleId) {
      setVehicleLoading(false);
      return undefined;
    }
    setVehicleLoading(true);
    const timer = setTimeout(() => { setVehicleLoading(false); }, 650);
    return () => clearTimeout(timer);
  }, [isLocationSelectionMode, selectedVehicleId]);

  useEffect(() => {
    if (!isLocationSelectionMode) {
      clearLocationMapMarkers();
      locationMapInstanceRef.current = null;
    }
  }, [isLocationSelectionMode]);

  useEffect(() => {
    if (!isTrackVisualReportActive) {
      clearTrackMapArtifacts();
      trackMapInstanceRef.current = null;
    }
  }, [isTrackVisualReportActive]);

  useEffect(() => {
    if (activeReport !== 'statistics') return () => { };
    if (!selectedVehicle?.plate) {
      setStatisticsData(null);
      setStatisticsError('');
      setStatisticsLoading(false);
      return () => { };
    }
    if (!isUnixDateRangeValid) {
      setStatisticsData(null);
      setStatisticsError('Choose a valid date range to load statistics.');
      setStatisticsLoading(false);
      return () => { };
    }

    let isMounted = true;
    const payload = { registration_num: selectedVehicle.plate, timeBegin: unixDateRange.from, timeEnd: unixDateRange.to };

    const fetchStatistics = async () => {
      setStatisticsLoading(true);
      setStatisticsError('');
      try {
        const response = await APIService.getFleetStatistics(payload);
        const normalizedStatistics = normalizeFleetStatistics(response);
        if (isMounted) {
          if (normalizedStatistics.hasData) {
            setStatisticsData({ vehicleData: normalizedStatistics.vehicleData, totals: normalizedStatistics.totals });
          } else {
            setStatisticsData(null);
            setStatisticsError('No statistics found for the selected range.');
          }
        }
      } catch (error) {
        if (isMounted) {
          console.log('fleet statistics error', error?.message || error);
          setStatisticsData(null);
          setStatisticsError('Unable to load statistics right now. Please try again.');
        }
      } finally {
        if (isMounted) setStatisticsLoading(false);
      }
    };

    fetchStatistics();
    return () => { isMounted = false; };
  }, [activeReport, isUnixDateRangeValid, selectedVehicle?.plate, unixDateRange.from, unixDateRange.to]);

  useEffect(() => {
    if (activeReport !== 'fuel') return () => { };
    if (!selectedVehicle?.plate) {
      setFuelEventsData(null);
      setFuelEventsError('');
      setFuelEventsLoading(false);
      return () => { };
    }
    if (!isUnixDateRangeValid) {
      setFuelEventsData(null);
      setFuelEventsError('Choose a valid date range to load fuel events.');
      setFuelEventsLoading(false);
      return () => { };
    }

    let isMounted = true;
    const payload = {
      registration_num: selectedVehicle.plate,
      timeBegin: fuelDateRange.from,
      timeEnd: fuelDateRange.to,
      tz: DEFAULT_FUEL_REPORT_TIMEZONE,
      locale: DEFAULT_FUEL_REPORT_LOCALE,
      rows: FUEL_EVENTS_PAGE_SIZE,
      page: 1,
      sidx: 'startDate',
      sord: 'asc'
    };

    const fetchFuelEvents = async () => {
      setFuelEventsLoading(true);
      setFuelEventsError('');
      try {
        const response = await APIService.getFleetFuelDraining(payload);
        if (!isMounted) return;

        const normalizedFuelEvents = normalizeFuelEventsReport(response);
        if (normalizedFuelEvents.rows.length) {
          setFuelEventsData(normalizedFuelEvents);
          setFuelEventsError('');
        } else {
          setFuelEventsData(null);
          setFuelEventsError('No filling or draining events were returned for the selected range.');
        }
      } catch (error) {
        if (isMounted) {
          console.log('fleet fuel events error', error?.message || error);
          setFuelEventsData(null);
          setFuelEventsError('Unable to load fuel events right now. Please try again.');
        }
      } finally {
        if (isMounted) setFuelEventsLoading(false);
      }
    };

    fetchFuelEvents();
    return () => { isMounted = false; };
  }, [activeReport, fuelDateRange.from, fuelDateRange.to, isUnixDateRangeValid, selectedVehicle?.plate]);

  useEffect(() => {
    if (activeReport !== 'track') return () => { };
    if (!selectedVehicle?.plate) {
      setTrackData(null);
      setTrackError('');
      setTrackLoading(false);
      return () => { };
    }
    if (!isUnixDateRangeValid) {
      setTrackData(null);
      setTrackError('Choose a valid date range to load track data.');
      setTrackLoading(false);
      return () => { };
    }

    let isMounted = true;
    const payload = {
      registration_num: selectedVehicle.plate,
      timeBegin: unixDateRange.from,
      timeEnd: unixDateRange.to
    };

    const fetchTrack = async () => {
      loadGoogleMapsConfig();
      setTrackLoading(true);
      setTrackError('');
      try {
        const response = await APIService.getFleetTrack(payload);
        if (!isMounted) return;

        const normalizedTrack = normalizeTrackReport(response);
        const normalizedLastKnown = normalizeTrackLastKnownPosition(response)
          || normalizedTrack.points[normalizedTrack.points.length - 1]
          || null;

        if (normalizedTrack.hasData || normalizedLastKnown) {
          setTrackData({
            ...normalizedTrack,
            lastKnownPosition: normalizedLastKnown
          });
          setTrackError('');
        } else {
          setTrackData(null);
          setTrackError('No track points were returned for the selected range.');
        }
      } catch (error) {
        if (isMounted) {
          console.log('fleet track error', error?.message || error);
          setTrackData(null);
          setTrackError('Unable to load track data right now. Please try again.');
        }
      } finally {
        if (isMounted) setTrackLoading(false);
      }
    };

    fetchTrack();
    return () => { isMounted = false; };
  }, [activeReport, isUnixDateRangeValid, loadGoogleMapsConfig, selectedVehicle?.plate, unixDateRange.from, unixDateRange.to]);

  useEffect(() => {
    if (activeReport !== 'trackFuel') return () => { };
    if (!selectedVehicle?.plate) {
      setTrackFuelData(null);
      setTrackFuelError('');
      setTrackFuelLoading(false);
      return () => { };
    }
    if (!isUnixDateRangeValid) {
      setTrackFuelData(null);
      setTrackFuelError('Choose a valid date range to load track and fuel data.');
      setTrackFuelLoading(false);
      return () => { };
    }

    let isMounted = true;
    const payload = {
      registration_num: selectedVehicle.plate,
      timeBegin: unixDateRange.from,
      timeEnd: unixDateRange.to
    };
    const fuelLevelPayload = {
      registration_num: selectedVehicle.plate,
      timeBegin: unixDateRange.from,
      timeEnd: unixDateRange.to,
      from: fuelDateRange.from,
      to: fuelDateRange.to
    };

    const fetchTrackFuel = async () => {
      loadGoogleMapsConfig();
      setTrackFuelLoading(true);
      setTrackFuelError('');
      try {
        const [trackFuelResult, fuelLevelResult] = await Promise.allSettled([
          APIService.getFleetTrackFuel(payload),
          APIService.getFleetFuelLevel(fuelLevelPayload)
        ]);
        if (!isMounted) return;

        if (trackFuelResult.status !== 'fulfilled') throw trackFuelResult.reason;

        const normalizedTrackFuel = normalizeTrackReport(trackFuelResult.value);
        const normalizedLastKnown = normalizedTrackFuel.points[normalizedTrackFuel.points.length - 1] || null;
        const normalizedFuelLevelSeries = fuelLevelResult.status === 'fulfilled'
          ? normalizeFuelLevelSeries(fuelLevelResult.value)
          : {
            hasData: false,
            actualSeries: [],
            smoothedSeries: [],
            tankCapacity: null,
            tankNumber: null
          };

        if (normalizedTrackFuel.hasData || normalizedLastKnown) {
          setTrackFuelData({
            ...normalizedTrackFuel,
            lastKnownPosition: normalizedLastKnown,
            fuelLevelSeries: normalizedFuelLevelSeries
          });
          setTrackFuelError('');
        } else {
          setTrackFuelData(null);
          setTrackFuelError('No track or fuel points were returned for the selected range.');
        }
      } catch (error) {
        if (isMounted) {
          console.log('fleet track fuel error', error?.message || error);
          setTrackFuelData(null);
          setTrackFuelError('Unable to load track and fuel data right now. Please try again.');
        }
      } finally {
        if (isMounted) setTrackFuelLoading(false);
      }
    };

    fetchTrackFuel();
    return () => { isMounted = false; };
  }, [activeReport, fuelDateRange.from, fuelDateRange.to, isUnixDateRangeValid, loadGoogleMapsConfig, selectedVehicle?.plate, unixDateRange.from, unixDateRange.to]);

  const renderVehicleLoading = () => (
    <Stack spacing={3}>
      <Card sx={{
        p: 2.5, borderRadius: 4, border: '1px solid rgba(148, 163, 184, 0.16)', boxShadow: '0 20px 40px rgba(15, 23, 42, 0.06)'
      }}
      >
        <Stack spacing={2.5}>
          <Skeleton variant="text" width={280} height={42} />
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {[1, 2, 3].map((item) => <Skeleton key={item} variant="rounded" width={120} height={32} />)}
          </Stack>
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map((item) => (
              <Grid item xs={12} sm={6} xl={3} key={item}>
                <Skeleton variant="rounded" height={120} sx={{ borderRadius: 3 }} />
              </Grid>
            ))}
          </Grid>
          <Grid container spacing={2.5}>
            <Grid item xs={12} xl={7}>
              <Skeleton variant="rounded" height={260} sx={{ borderRadius: 3.5 }} />
            </Grid>
            <Grid item xs={12} xl={5}>
              <Skeleton variant="rounded" height={260} sx={{ borderRadius: 3.5 }} />
            </Grid>
          </Grid>
          <Skeleton variant="rounded" height={280} sx={{ borderRadius: 3.5 }} />
        </Stack>
      </Card>
    </Stack>
  );

  const renderFleetHeaderLoading = () => (
    <Card sx={{
      p: 2.5, borderRadius: 4, border: '1px solid rgba(148, 163, 184, 0.16)', boxShadow: '0 18px 34px rgba(15, 23, 42, 0.05)'
    }}
    >
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A' }}>Loading live fleet status...</Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: '#64748B' }}>Pulling current state from Omnicomm. Vehicles will appear once sync completes.</Typography>
        </Box>
        <LinearProgress sx={{ height: 7, borderRadius: 999 }} />
        <Grid container spacing={1.5}>
          {[1, 2, 3, 4, 5].map((item) => (
            <Grid item xs={12} sm={6} lg={2.4} key={`summary-skeleton-${item}`}>
              <Skeleton variant="rounded" height={120} sx={{ borderRadius: 3.5 }} />
            </Grid>
          ))}
        </Grid>
      </Stack>
    </Card>
  );

  const renderNoFleetData = () => (
    <Card sx={{
      p: 2.5, borderRadius: 4, border: '1px dashed rgba(148, 163, 184, 0.32)', boxShadow: 'none'
    }}
    >
      <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A' }}>No live fleet data found</Typography>
      <Typography variant="body2" sx={{ mt: 1, color: '#64748B' }}>Fleet status API returned no vehicles. Please check the API response or refresh.</Typography>
    </Card>
  );

  const renderNoVehicleSelected = () => (
    <Card sx={{
      p: { xs: 2.2, md: 2.8 }, borderRadius: 4, border: '1px dashed rgba(148, 163, 184, 0.32)', boxShadow: 'none', background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)'
    }}
    >
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A' }}>Pick a vehicle to unlock live insights</Typography>
          <Typography variant="body2" sx={{ mt: 0.9, color: '#64748B' }}>Use the vehicle rail to the left to keep selection and analysis visible at the same time.</Typography>
        </Box>
      </Stack>
    </Card>
  );

  const shouldShowDateTimeFilter = activeReport === 'statistics' || activeReport === 'track' || activeReport === 'trackFuel' || activeReport === 'fuel';

  const renderVehicleSelectionRail = () => (
    <Card sx={{
      borderRadius: 4,
      border: '1px solid rgba(148, 163, 184, 0.16)',
      boxShadow: 'none',
      overflow: 'hidden',
      position: { lg: isTrackVisualReportActive ? 'static' : 'sticky' },
      top: { lg: 84 }
    }}
    >
      <Box sx={{ p: 1.5, borderBottom: '1px solid rgba(148, 163, 184, 0.14)', bgcolor: '#FFFFFF' }}>
        <Stack spacing={1.2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#0F172A' }}>Vehicles</Typography>
            <Tooltip title={isRefreshingFleet ? 'Refreshing vehicle data' : 'Refresh vehicle data'} arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={() => fetchCurrentState(false)}
                  disabled={isRefreshingFleet}
                  sx={{
                    width: 38,
                    height: 38,
                    color: isRefreshingFleet ? '#1D4ED8' : '#334155',
                    bgcolor: isRefreshingFleet ? alpha('#2563EB', 0.12) : '#F8FAFC',
                    border: `1px solid ${isRefreshingFleet ? alpha('#2563EB', 0.24) : 'rgba(148, 163, 184, 0.18)'}`,
                    boxShadow: isRefreshingFleet ? '0 10px 22px rgba(37, 99, 235, 0.18)' : 'none',
                    transition: 'transform 160ms ease, background-color 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
                    '&:hover': {
                      bgcolor: alpha('#2563EB', 0.1),
                      borderColor: alpha('#2563EB', 0.28),
                      transform: 'translateY(-1px)',
                      boxShadow: '0 12px 24px rgba(37, 99, 235, 0.16)'
                    }
                  }}
                >
                  <RefreshRoundedIcon
                    sx={{
                      fontSize: 20,
                      animation: isRefreshingFleet ? 'fleetSpin 0.9s linear infinite' : 'none',
                      '@keyframes fleetSpin': {
                        from: { transform: 'rotate(0deg)' },
                        to: { transform: 'rotate(360deg)' }
                      }
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.8}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                flexShrink: 0,
                bgcolor: isRefreshingFleet ? '#2563EB' : '#10B981',
                boxShadow: `0 0 0 4px ${alpha(isRefreshingFleet ? '#2563EB' : '#10B981', 0.12)}`
              }}
            />
            <Typography variant="caption" sx={{ color: '#475569', fontWeight: 700 }}>
              {isRefreshingFleet ? 'Refreshing live vehicle and map data...' : refreshStatusLabel}
            </Typography>
          </Stack>
          <TextField
            fullWidth
            size="small"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder="Search vehicle number"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ color: '#94A3B8' }} />
                </InputAdornment>
              )
            }}
          />
          <Stack direction="row" spacing={0.8} sx={{ flexWrap: 'wrap', gap: 0.8 }}>
            {[
              ['all', 'All', fleetCounts.total, theme.palette.primary.main],
              ['active', 'Active', fleetCounts.active, STATUS_META.active.color],
              ['idle', 'Idle', fleetCounts.idle, STATUS_META.idle.color],
              ['offline', 'Offline', fleetCounts.offline, STATUS_META.offline.color]
            ].map(([value, label, count, color]) => {
              const isActiveFilter = statusFilter === value;
              const activeTextColor = value === 'all'
                ? '#1E40AF'
                : STATUS_META[value].accent;

              return (
                <Chip
                  key={value}
                  clickable
                  size="small"
                  label={`${label} (${count})`}
                  onClick={() => setStatusFilter(value)}
                  variant={isActiveFilter ? 'filled' : 'outlined'}
                  sx={{
                    fontWeight: 700,
                    borderColor: alpha(color, 0.4),
                    bgcolor: isActiveFilter ? alpha(color, 0.16) : '#FFFFFF',
                    color: isActiveFilter ? activeTextColor : '#334155'
                  }}
                />
              );
            })}
          </Stack>
        </Stack>
      </Box>

      <Box sx={{
        p: 1,
        maxHeight: { xs: 420, lg: 'calc(100vh - 280px)' },
        overflowY: 'auto',
        bgcolor: '#F8FAFC'
      }}
      >
        {filteredVehicles.length ? (
          <Stack spacing={0.8}>
            {filteredVehicles.map((vehicle) => (
              <VehicleListItem
                key={vehicle.id}
                vehicle={vehicle}
                selected={selectedVehicleIds.includes(vehicle.id)}
                onClick={() => handleVehicleSelect(vehicle.id)}
              />
            ))}
          </Stack>
        ) : (
          <Card sx={{
            p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.24)', boxShadow: 'none'
          }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0F172A' }}>{vehicles.length ? 'No vehicles matched' : 'No live vehicles available'}</Typography>
            <Typography variant="body2" sx={{ mt: 0.75, color: '#64748B' }}>Try another search or status filter.</Typography>
          </Card>
        )}
      </Box>
    </Card>
  );

  const renderTrackReport = () => {
    const {
      currentPoint,
      endPoint,
      totalPoints
    } = trackSummary;
    const reportTitle = isTrackFuelReportActive ? 'Track + Fuel' : 'Track';
    const invalidRangeLabel = isTrackFuelReportActive ? 'track and fuel data' : 'track data';
    const lastKnownTimeLabel = formatTrackTimestamp(
      currentPoint?.timestamp || currentPoint?.eventDate || endPoint?.timestamp,
      formatSyncDate(selectedVehicle?.lastSyncAt, selectedVehicle?.lastSyncMinutes)
    );
    const activeIgnitionGroup = selectedTrackEventGroup
      ? trackIgnitionGroups.find((group) => group.id === selectedTrackEventGroup.id) || null
      : null;
    const activeIgnitionEvents = activeIgnitionGroup?.events || [];
    const selectedLocationPoint = selectedTrackPoint?.point || null;
    const selectedLocationTitle = selectedTrackPoint?.title || 'Location point';
    const playbackSliderMax = Math.max(totalPoints - 1, 0);
    const playbackTimestampLabel = formatTrackTimestamp(
      trackPlaybackPoint?.timestamp || trackPlaybackPoint?.eventDate || trackPlaybackPoint?.rawTimestamp,
      lastKnownTimeLabel
    );

    const renderTrackMapBody = () => {
      if (isMapKeyLoading) {
        return (
          <Stack spacing={1.2}>
            <Typography variant="body2" sx={{ color: '#475569', fontWeight: 700 }}>Loading map...</Typography>
            <LinearProgress sx={{ height: 8, borderRadius: 999 }} />
          </Stack>
        );
      }

      if (mapError) {
        return (
          <Stack spacing={1.1} alignItems="flex-start">
            <Typography variant="body2" sx={{ color: '#B91C1C', fontWeight: 700 }}>{mapError}</Typography>
            <Button variant="contained" size="small" onClick={handleRetryLocationMap}>Retry map load</Button>
          </Stack>
        );
      }

      if (!googleMapsKey) {
        return (
          <Stack spacing={1.2}>
            <Typography variant="body2" sx={{ color: '#475569', fontWeight: 700 }}>Loading map...</Typography>
            <LinearProgress sx={{ height: 8, borderRadius: 999 }} />
          </Stack>
        );
      }

      return (
        <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
          <Box ref={trackMapContainerRef} sx={TRACK_MAP_CONTAINER_STYLE} />

          {isTrackVisualReportActive && (
            <>
              {activeIgnitionGroup && (
                <Box sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  zIndex: 2,
                  width: { xs: 'calc(100% - 32px)', sm: 280, md: 270 },
                  maxHeight: { xs: 260, md: 250 },
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.97)',
                  border: '1px solid rgba(148, 163, 184, 0.2)',
                  boxShadow: '0 10px 24px rgba(15, 23, 42, 0.14)',
                  backdropFilter: 'blur(10px)'
                }}
                >
                  <Box sx={{ p: 1, borderBottom: '1px solid rgba(148, 163, 184, 0.14)' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={0.8}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ color: '#0284C7', fontWeight: 800, lineHeight: 1.1 }}>
                          {selectedVehicle?.plate}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mt: 0.25 }}>
                          {formatCoordinateLabel(activeIgnitionGroup.latitude)}
                          {', '}
                          {formatCoordinateLabel(activeIgnitionGroup.longitude)}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => setSelectedTrackEventGroup(null)}
                        sx={{ color: '#64748B', mt: -0.2, mr: -0.3 }}
                      >
                        <CloseRoundedIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
                      <Chip
                        size="small"
                        label={`${activeIgnitionGroup.eventCount} events`}
                        sx={{
                          bgcolor: '#FFF7ED',
                          color: '#C2410C',
                          fontWeight: 700,
                          height: 22
                        }}
                      />
                      <Chip
                        size="small"
                        label={`${activeIgnitionGroup.ignitionOnCount} on`}
                        sx={{
                          bgcolor: '#DBEAFE',
                          color: '#1D4ED8',
                          fontWeight: 700,
                          height: 22
                        }}
                      />
                      <Chip
                        size="small"
                        label={`${activeIgnitionGroup.ignitionOffCount} off`}
                        sx={{
                          bgcolor: '#E0E7FF',
                          color: '#4338CA',
                          fontWeight: 700,
                          height: 22
                        }}
                      />
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      overflowY: 'auto',
                      p: 0.8
                    }}
                  >
                    <Stack spacing={0.55}>
                      {activeIgnitionEvents.map((event) => (
                        <Box
                          key={event.id}
                          sx={{
                            px: 0.85,
                            py: 0.62,
                            borderRadius: 1.6,
                            border: '1px solid rgba(148, 163, 184, 0.14)',
                            bgcolor: '#FFFFFF'
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" spacing={1}>
                            <Typography variant="caption" sx={{ color: '#334155', fontWeight: 700 }}>
                              {event.type === 5 ? 'Ignition On' : 'Ignition Off'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#475569', fontWeight: 700, whiteSpace: 'nowrap' }}>
                              {formatTrackTimestamp(event.timestamp || event.eventDateValue)}
                            </Typography>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              )}

              {selectedLocationPoint && (
                <Box sx={{
                  position: 'absolute',
                  top: activeIgnitionGroup ? { xs: 288, md: 278 } : 16,
                  right: 16,
                  zIndex: 2,
                  width: { xs: 'calc(100% - 32px)', sm: 280, md: 270 },
                  maxHeight: { xs: 250, md: 238 },
                  overflow: 'hidden',
                  borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.97)',
                  border: '1px solid rgba(148, 163, 184, 0.2)',
                  boxShadow: '0 10px 24px rgba(15, 23, 42, 0.14)',
                  backdropFilter: 'blur(10px)'
                }}
                >
                  <Box sx={{ p: 0.9 }}>
                    <Stack direction="row" justifyContent="space-between" spacing={0.7} alignItems="flex-start">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
                          {selectedLocationTitle}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#475569', display: 'block', mt: 0.15 }}>
                          {formatCoordinateLabel(selectedLocationPoint.latitude)}
                          {', '}
                          {formatCoordinateLabel(selectedLocationPoint.longitude)}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedTrackPoint(null);
                          setSelectedTrackPointDetails(null);
                          setSelectedTrackPointDetailsError('');
                        }}
                        sx={{ color: '#64748B', mt: -0.2, mr: -0.3 }}
                      >
                        <CloseRoundedIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#64748B',
                        fontWeight: 700,
                        mt: 0.45,
                        display: 'block'
                      }}
                    >
                      Location details
                    </Typography>
                    <Box
                      sx={{
                        mt: 0.55,
                        maxHeight: { xs: 165, md: 152 },
                        overflowY: 'auto',
                        pr: 0.35
                      }}
                    >
                      {(() => {
                        if (selectedTrackPointDetailsLoading) {
                          return (
                            <Stack spacing={0.7}>
                              <LinearProgress sx={{ height: 5, borderRadius: 999 }} />
                              <Typography variant="caption" sx={{ color: '#64748B' }}>
                                Loading...
                              </Typography>
                            </Stack>
                          );
                        }

                        if (selectedTrackPointDetailsError) {
                          return (
                            <Typography variant="caption" sx={{ display: 'block', color: '#B91C1C' }}>
                              {selectedTrackPointDetailsError}
                            </Typography>
                          );
                        }

                        return (
                          <Stack spacing={0.45}>
                            <Typography variant="caption" sx={{ color: '#334155' }}>
                              Time:
                              {' '}
                              {formatTrackTimestamp(
                                selectedLocationPoint.timestamp
                                || selectedLocationPoint.eventDate
                                || selectedLocationPoint.rawTimestamp,
                                '--'
                              )}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#334155' }}>
                              Speed:
                              {' '}
                              {Number.isFinite(Number(selectedLocationPoint.speed)) ? `${Number(selectedLocationPoint.speed)} km/h` : '--'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#334155' }}>
                              Fuel level:
                              {' '}
                              {formatFuelVolume(selectedLocationPoint.fuelVolume)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#334155' }}>
                              Ignition:
                              {' '}
                              {getTrackBooleanLabel(selectedLocationPoint.ignition, 'On', 'Off')}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#334155' }}>
                              Address:
                              {' '}
                              {selectedTrackPointDetails?.address || '--'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#334155' }}>
                              Engine:
                              {' '}
                              {selectedTrackPointDetails?.engineWork || '--'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#334155' }}>
                              Fuel temp:
                              {' '}
                              {selectedTrackPointDetails?.tempFuel !== null && selectedTrackPointDetails?.tempFuel !== undefined ? `${selectedTrackPointDetails.tempFuel} deg C` : '--'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#334155' }}>
                              Voltage:
                              {' '}
                              {selectedTrackPointDetails?.voltage !== null && selectedTrackPointDetails?.voltage !== undefined ? `${selectedTrackPointDetails.voltage} V` : '--'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#334155' }}>
                              Mileage CAN:
                              {' '}
                              {selectedTrackPointDetails?.mileageCAN !== null && selectedTrackPointDetails?.mileageCAN !== undefined ? `${selectedTrackPointDetails.mileageCAN}` : '--'}
                            </Typography>
                          </Stack>
                        );
                      })()}
                    </Box>
                  </Box>
                </Box>
              )}

              {totalPoints > 1 && (
                <Box sx={{
                  position: 'absolute',
                  top: 14,
                  left: 14,
                  zIndex: 2,
                  width: { xs: 'calc(100% - 28px)', sm: 360, md: 420 },
                  px: 1,
                  py: 0.8,
                  bgcolor: 'rgba(255,255,255,0.95)',
                  border: '1px solid rgba(148, 163, 184, 0.2)',
                  borderRadius: 2,
                  boxShadow: '0 10px 24px rgba(15, 23, 42, 0.12)',
                  backdropFilter: 'blur(8px)'
                }}
                >
                  <Stack spacing={0.7}>
                    <Stack direction="row" spacing={0.8} alignItems="center" justifyContent="space-between">
                      <Stack direction="row" spacing={0.3} alignItems="center">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setTrackPlaybackActive(false);
                            setTrackPlaybackIndex((previousIndex) => Math.max(previousIndex - 1, 0));
                          }}
                        >
                          <SkipPreviousRoundedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (!totalPoints) return;
                            if (!isTrackPlaybackActive && clampedTrackPlaybackIndex >= playbackSliderMax) {
                              setTrackPlaybackIndex(0);
                            }
                            setTrackPlaybackActive((previousValue) => !previousValue);
                          }}
                          sx={{
                            width: 34,
                            height: 34,
                            bgcolor: '#0F172A',
                            color: '#FFFFFF',
                            '&:hover': { bgcolor: '#1E293B' }
                          }}
                        >
                          {isTrackPlaybackActive ? <PauseRoundedIcon fontSize="small" /> : <PlayArrowRoundedIcon fontSize="small" />}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setTrackPlaybackActive(false);
                            setTrackPlaybackIndex((previousIndex) => Math.min(previousIndex + 1, playbackSliderMax));
                          }}
                        >
                          <SkipNextRoundedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setTrackPlaybackActive(false);
                            setTrackPlaybackIndex(0);
                          }}
                        >
                          <StopRoundedIcon fontSize="small" />
                        </IconButton>
                      </Stack>

                      <Typography variant="caption" sx={{ color: '#475569', fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {playbackTimestampLabel}
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
                        Playback speed
                      </Typography>
                      <Stack direction="row" spacing={0.6} alignItems="center" useFlexGap flexWrap="wrap" justifyContent="flex-end">
                        {TRACK_PLAYBACK_SPEED_OPTIONS.map((option) => {
                          const isActiveSpeed = trackPlaybackSpeed === option.value;
                          return (
                            <Chip
                              key={option.value}
                              size="small"
                              clickable
                              label={option.label}
                              onClick={() => setTrackPlaybackSpeed(option.value)}
                              sx={{
                                height: 24,
                                fontWeight: 700,
                                borderRadius: 999,
                                bgcolor: isActiveSpeed ? '#F97316' : alpha('#F97316', 0.1),
                                color: isActiveSpeed ? '#FFFFFF' : '#C2410C',
                                border: `1px solid ${isActiveSpeed ? '#F97316' : alpha('#F97316', 0.22)}`,
                                '&:hover': {
                                  bgcolor: isActiveSpeed ? '#EA580C' : alpha('#F97316', 0.18)
                                }
                              }}
                            />
                          );
                        })}
                      </Stack>
                    </Stack>

                    <Box sx={{ width: '100%' }}>
                      <Box
                        component="input"
                        type="range"
                        min={0}
                        max={playbackSliderMax}
                        step={1}
                        value={clampedTrackPlaybackIndex}
                        onChange={(event) => {
                          setTrackPlaybackActive(false);
                          setTrackPlaybackIndex(Number(event.target.value) || 0);
                        }}
                        sx={{
                          width: '100%',
                          height: 4,
                          borderRadius: 999,
                          cursor: 'pointer',
                          accentColor: '#F97316',
                          '&::-webkit-slider-thumb': {
                            WebkitAppearance: 'none',
                            width: 14,
                            height: 14,
                            borderRadius: '50%',
                            background: '#F97316',
                            border: '2px solid #FFFFFF',
                            boxShadow: '0 0 0 1px rgba(249, 115, 22, 0.3)'
                          }
                        }}
                      />
                    </Box>
                  </Stack>
                </Box>
              )}
            </>
          )}

          {activeTrackError && (
            <Box sx={{
              position: 'absolute', inset: 0, bgcolor: 'rgba(248, 250, 252, 0.86)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2
            }}
            >
              <Stack spacing={1.1} alignItems="center" sx={{ maxWidth: 420, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 800 }}>
                  {reportTitle}
                  {' unavailable'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748B' }}>{activeTrackError}</Typography>
              </Stack>
            </Box>
          )}

          {!activeTrackError && activeTrackLoading && (
            <Box sx={{
              position: 'absolute', inset: 0, bgcolor: 'rgba(248, 250, 252, 0.82)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none'
            }}
            >
              <Stack spacing={1} alignItems="center" sx={{ minWidth: 220 }}>
                <Typography variant="body2" sx={{ color: '#475569', fontWeight: 700 }}>
                  {isTrackFuelReportActive ? 'Loading route and fuel data...' : 'Loading route data...'}
                </Typography>
                <LinearProgress sx={{ width: '100%', height: 7, borderRadius: 999 }} />
              </Stack>
            </Box>
          )}

          {!activeTrackError && !activeTrackLoading && !totalPoints && !currentPoint && (
            <Box sx={{
              position: 'absolute', inset: 0, bgcolor: 'rgba(248, 250, 252, 0.82)', display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
            >
              <Typography variant="body2" sx={{ color: '#475569', fontWeight: 700 }}>
                {isTrackFuelReportActive
                  ? 'No mappable route points were returned for this range.'
                  : 'No mappable track points were returned for this range.'}
              </Typography>
            </Box>
          )}

          {isMapScriptLoading && (
            <Box sx={{
              position: 'absolute', inset: 0, bgcolor: 'rgba(248, 250, 252, 0.82)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none'
            }}
            >
              <Stack spacing={1} alignItems="center" sx={{ minWidth: 220 }}>
                <Typography variant="body2" sx={{ color: '#475569', fontWeight: 700 }}>Loading route map...</Typography>
                <LinearProgress sx={{ width: '100%', height: 7, borderRadius: 999 }} />
              </Stack>
            </Box>
          )}
        </Box>
      );
    };

    if (!selectedVehicle?.plate) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Choose a vehicle</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            Select a vehicle to load
            {' '}
            {invalidRangeLabel}
            .
          </Typography>
        </Card>
      );
    }

    if (!isUnixDateRangeValid) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Invalid date range</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            Please choose a valid From and To date-time range to load
            {' '}
            {invalidRangeLabel}
            .
          </Typography>
        </Card>
      );
    }

    if (isTrackReportActive) {
      return (
        <Box sx={{
          height: { xs: 560, lg: 780 },
          borderRadius: 3.25,
          border: '1px solid rgba(148, 163, 184, 0.16)',
          overflow: 'hidden',
          position: 'relative',
          bgcolor: '#F8FAFC'
        }}
        >
          {renderTrackMapBody()}
        </Box>
      );
    }

    return (
      <Stack spacing={1.5}>
        <Card sx={{
          p: 0,
          borderRadius: 3.25,
          border: '1px solid rgba(148, 163, 184, 0.16)',
          boxShadow: 'none',
          overflow: 'hidden'
        }}
        >
          <Box sx={{
            height: { xs: 420, lg: 560 },
            borderRadius: 2.5,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#F8FAFC',
            position: 'relative'
          }}
          >
            {renderTrackMapBody()}
          </Box>
        </Card>

        <Card sx={{
          p: 0,
          borderRadius: 3.4,
          border: '1px solid rgba(148, 163, 184, 0.16)',
          boxShadow: '0 18px 44px rgba(15, 23, 42, 0.04)',
          overflow: 'hidden',
          bgcolor: '#FFFFFF',
          backgroundImage: 'linear-gradient(180deg, rgba(248, 250, 252, 0.85) 0%, rgba(255, 255, 255, 1) 100%)'
        }}
        >
          <Box sx={{ p: { xs: 1, md: 1.3 } }}>
            {trackFuelChartOptions ? (
              <Box sx={{ height: 440 }}>
                <ReactECharts options={trackFuelChartOptions} />
              </Box>
            ) : (
              <Stack
                spacing={0.8}
                alignItems="center"
                justifyContent="center"
                sx={{ minHeight: 260, color: '#64748B', textAlign: 'center' }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {activeTrackLoading ? 'Loading chart data...' : 'No fuel chart points were returned for this range.'}
                </Typography>
                <Typography variant="caption">{lastKnownTimeLabel}</Typography>
              </Stack>
            )}
          </Box>
        </Card>
      </Stack>
    );
  };

  const renderLocationReport = () => {
    const invalidCoordinatesCount = locationSelectedVehicles.length - locationMapMarkers.length;

    const renderLoadingState = () => (
      <Stack spacing={1.2}>
        <Typography variant="body2" sx={{ color: '#475569', fontWeight: 700 }}>Loading map...</Typography>
        <LinearProgress sx={{ height: 8, borderRadius: 999 }} />
      </Stack>
    );

    const renderMapBody = () => {
      if (isMapKeyLoading) return renderLoadingState();
      if (mapError) {
        return (
          <Stack spacing={1.1} alignItems="flex-start">
            <Typography variant="body2" sx={{ color: '#B91C1C', fontWeight: 700 }}>{mapError}</Typography>
            <Button variant="contained" size="small" onClick={handleRetryLocationMap}>Retry map load</Button>
          </Stack>
        );
      }
      if (!googleMapsKey) return renderLoadingState();

      // FIX: Cost Optimization - Keep Map container mounted even if there are no selections
      return (
        <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
          <Box ref={locationMapContainerRef} sx={LOCATION_MAP_CONTAINER_STYLE} />

          {!locationMapMarkers.length && (
            <Box sx={{
              position: 'absolute', inset: 0, bgcolor: 'rgba(248, 250, 252, 0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
            >
              <Typography variant="body2" sx={{ color: '#475569', fontWeight: 700 }}>Select a vehicle to view its location on the map.</Typography>
            </Box>
          )}

          {isMapScriptLoading && (
            <Box sx={{
              position: 'absolute', inset: 0, bgcolor: 'rgba(248, 250, 252, 0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none'
            }}
            >
              <Stack spacing={1} alignItems="center" sx={{ minWidth: 220 }}>
                <Typography variant="body2" sx={{ color: '#475569', fontWeight: 700 }}>Loading map...</Typography>
                <LinearProgress sx={{ width: '100%', height: 7, borderRadius: 999 }} />
              </Stack>
            </Box>
          )}
        </Box>
      );
    };

    return (
      <Grid container spacing={1.5}>
        <Grid item xs={12}>
          <Card sx={{
            p: 1.5, borderRadius: 3.25, border: '1px solid rgba(148, 163, 184, 0.16)', boxShadow: 'none'
          }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ color: '#0F172A', fontWeight: 800 }}>Live Location Map</Typography>
              <Stack direction="row" spacing={0.7}>
                <Chip size="small" label={`${locationMapMarkers.length} mapped`} sx={{ bgcolor: alpha('#2563EB', 0.1), color: '#1E40AF', fontWeight: 700 }} />
                {invalidCoordinatesCount > 0 && <Chip size="small" label={`${invalidCoordinatesCount} missing`} sx={{ bgcolor: alpha('#D97706', 0.12), color: '#92400E', fontWeight: 700 }} />}
              </Stack>
            </Stack>
            <Box sx={{
              height: { xs: 420, md: 520 }, borderRadius: 2.5, border: '1px solid rgba(148, 163, 184, 0.18)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F8FAFC'
            }}
            >
              {renderMapBody()}
            </Box>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderStatisticsReport = () => {
    const fromMoment = moment(dateRange.fromDateTime, DATE_TIME_INPUT_FORMAT, true);
    const toMoment = moment(dateRange.toDateTime, DATE_TIME_INPUT_FORMAT, true);
    const selectedRangeLabel = fromMoment.isValid() && toMoment.isValid()
      ? `${fromMoment.format('DD MMM YYYY, hh:mm A')} to ${toMoment.format('DD MMM YYYY, hh:mm A')}`
      : 'Custom range';

    if (!isUnixDateRangeValid) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Invalid date range</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            Please choose a valid From and To date-time range to load statistics.
          </Typography>
        </Card>
      );
    }

    if (isStatisticsLoading) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}><Skeleton variant="rounded" height={190} sx={{ borderRadius: 3 }} /></Grid>
        </Grid>
      );
    }

    if (statisticsError) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Statistics unavailable</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>{statisticsError}</Typography>
        </Card>
      );
    }

    if (!statisticsData) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>No statistics found</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            No records were returned for
            {' '}
            {selectedVehicle?.plate}
            {' '}
            in the selected range.
          </Typography>
        </Card>
      );
    }

    const vehicleStats = statisticsData?.vehicleData || {};
    const fuelStats = vehicleStats?.fuel || vehicleStats;
    const totalsStats = statisticsData?.totals || {};
    const secondaryEngineStats = Array.isArray(vehicleStats?.uniDataList)
      ? vehicleStats.uniDataList[0]
      : null;

    const renderStatisticsSection = (title, rows) => (
      <Card sx={{
        p: 1.45, borderRadius: 3, border: '1px solid rgba(148, 163, 184, 0.16)', boxShadow: 'none', height: '100%'
      }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0F172A', mb: 1 }}>
          {title}
        </Typography>
        <Grid container spacing={0.8}>
          {rows.map(([label, value]) => (
            <Grid item xs={12} sm={6} key={`${title}-${label}`}>
              <Box sx={{
                p: 0.85, borderRadius: 1.8, border: '1px solid rgba(148, 163, 184, 0.14)', bgcolor: '#FFFFFF', minHeight: 74
              }}
              >
                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700, lineHeight: 1.28 }}>
                  {label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.45, color: '#0F172A', fontWeight: 800, lineHeight: 1.32
                  }}
                >
                  {value}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Card>
    );

    const movementAndOperationRows = [
      ['Mileage, km', formatDecimalValue(totalsStats.averageMileage ?? vehicleStats.mileage, 2)],
      ['Engine operation time, hh:mm:ss (% of report period)', formatDurationWithPercent(totalsStats.totalWorked ?? vehicleStats.worked, totalsStats.totalWorkedPercent ?? vehicleStats.workedPercent)],
      ['Engine off time, hh:mm:ss (% of report period)', formatDurationWithPercent(totalsStats.totalLayUpTime ?? vehicleStats.layUp, totalsStats.totalLayUpTimePercent ?? vehicleStats.layUpPercent)],
      ['Maximum speed, km/h', formatDecimalValue(totalsStats.maxSpeed ?? vehicleStats.maxSpeed, 1)]
    ];

    const mainTankRows = [
      ['Initial volume, l', formatTenthsValue(fuelStats.startVolume)],
      ['Refueling volume, l', formatTenthsValue(totalsStats.totalRefuelling ?? fuelStats.refuelling)],
      ['Actual mileage per 1 l, km', formatDecimalValue(totalsStats.totalMileagePerLitre ?? vehicleStats.mileagePerLitre, 2)],
      ['Final volume, l', formatTenthsValue(fuelStats.endVolume)],
      ['Draining volume, l', formatTenthsValue(totalsStats.totalDraining ?? fuelStats.draining)],
      ['Fuel consumption per engine hour, l', formatTenthsValue(totalsStats.consPerMHEngineOn ?? vehicleStats.fuelConsPerMotorhour)],
      ['Actual consumption, l', formatTenthsValue(totalsStats.totalConsumption ?? fuelStats.fuelConsumption)]
    ];

    const secondaryEngineRows = [
      ['Operation time, hh:mm:ss', formatDurationWithPercent(secondaryEngineStats?.univInputOnTime, secondaryEngineStats?.univInputOnTimePercent)],
      ['Consumption with auxiliary equipment ON, l', formatTenthsValue(secondaryEngineStats?.univInputOnConsumption)],
      ['Consumption with auxiliary equipment ON per engine hour, l', formatTenthsValue(secondaryEngineStats?.univInputHourConsumption)],
      ['Downtime, hh:mm:ss', formatDurationWithPercent(secondaryEngineStats?.univInputOffTime, secondaryEngineStats?.univInputOffTimePercent)]
    ];

    return (
      <Stack spacing={1.25}>
        <Card sx={{
          p: 1.2, borderRadius: 3, border: '1px solid rgba(148, 163, 184, 0.18)', boxShadow: 'none', bgcolor: '#F8FAFC'
        }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={0.7}
            justifyContent="space-between"
            alignItems={{ md: 'center' }}
          >
            <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 800 }}>
              {statisticsData?.vehicleData?.name || selectedVehicle?.plate}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
              {selectedRangeLabel}
            </Typography>
          </Stack>
        </Card>

        <Grid container spacing={1}>
          <Grid item xs={12} lg={6}>
            {renderStatisticsSection('Movement and operation', movementAndOperationRows)}
          </Grid>
          <Grid item xs={12} lg={6}>
            {renderStatisticsSection('Fuel (main tank)', mainTankRows)}
          </Grid>
          <Grid item xs={12}>
            {renderStatisticsSection('Activity Secondary Engine', secondaryEngineRows)}
          </Grid>
        </Grid>
      </Stack>
    );
  };

  const renderFuelReport = () => {
    const fromMoment = moment(dateRange.fromDateTime, DATE_TIME_INPUT_FORMAT, true);
    const toMoment = moment(dateRange.toDateTime, DATE_TIME_INPUT_FORMAT, true);
    const selectedRangeLabel = fromMoment.isValid() && toMoment.isValid()
      ? `${fromMoment.format('DD MMM YYYY, hh:mm A')} to ${toMoment.format('DD MMM YYYY, hh:mm A')}`
      : 'Custom range';

    if (!selectedVehicle?.plate) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Choose a vehicle</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            Select a vehicle to load refilling and draining events.
          </Typography>
        </Card>
      );
    }

    if (!isUnixDateRangeValid) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Invalid date range</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            Please choose a valid From and To date-time range to load fuel events.
          </Typography>
        </Card>
      );
    }

    if (isFuelEventsLoading) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}><Skeleton variant="rounded" height={190} sx={{ borderRadius: 3 }} /></Grid>
          <Grid item xs={12}><Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} /></Grid>
        </Grid>
      );
    }

    if (fuelEventsError) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>Fuel events unavailable</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>{fuelEventsError}</Typography>
        </Card>
      );
    }

    const fuelRows = fuelEventsData?.rows || [];
    if (!fuelRows.length) {
      return (
        <Card sx={{
          p: 2, borderRadius: 3, border: '1px dashed rgba(148, 163, 184, 0.28)', boxShadow: 'none'
        }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>No fuel events found</Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: '#64748B' }}>
            No records were returned for
            {' '}
            {selectedVehicle.plate}
            {' '}
            in the selected range.
          </Typography>
        </Card>
      );
    }

    const refillingRows = fuelRows.filter((row) => row.type === 1);
    const drainingRows = fuelRows.filter((row) => row.type === 3);
    const totalRefillingVolume = refillingRows.reduce((total, row) => total + (Number(row.volume) || 0), 0);
    const totalDrainingVolume = drainingRows.reduce((total, row) => total + (Number(row.volume) || 0), 0);
    const selectedFuelEventTitle = selectedFuelEventMapRow?.typeLabel || 'Fuel event';
    const selectedFuelEventCoordinates = selectedFuelEventMapRow
      ? `${formatCoordinateLabel(selectedFuelEventMapRow.latitude)}, ${formatCoordinateLabel(selectedFuelEventMapRow.longitude)}`
      : '--';

    return (
      <Stack spacing={1.25}>
        <Card sx={{
          p: 1.2, borderRadius: 3, border: '1px solid rgba(148, 163, 184, 0.18)', boxShadow: 'none', bgcolor: '#F8FAFC'
        }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={0.7}
            justifyContent="space-between"
            alignItems={{ md: 'center' }}
          >
            <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 800 }}>
              {selectedVehicle.plate}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
              {selectedRangeLabel}
            </Typography>
          </Stack>
        </Card>

        <Grid container spacing={1}>
          <Grid item xs={12} md={4}>
            <DetailMetricCard
              label="Total events"
              value={`${fuelRows.length}`}
              caption={`${fuelEventsData?.totalRecords || fuelRows.length} records returned`}
              icon={LocalGasStationRoundedIcon}
              color="#EA580C"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <DetailMetricCard
              label="Refilling"
              value={formatFuelVolume(totalRefillingVolume)}
              caption={`${refillingRows.length} events`}
              icon={LocalGasStationRoundedIcon}
              color="#16A34A"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <DetailMetricCard
              label="Draining"
              value={formatFuelVolume(totalDrainingVolume)}
              caption={`${drainingRows.length} events`}
              icon={LocalGasStationRoundedIcon}
              color="#DC2626"
            />
          </Grid>
        </Grid>

        {selectedFuelEventMapRow && (
          <Card sx={{
            p: 1.1,
            borderRadius: 3,
            border: '1px solid rgba(148, 163, 184, 0.16)',
            boxShadow: 'none',
            overflow: 'hidden'
          }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 0.9 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 800 }}>
                  Event location map
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                  {selectedFuelEventTitle}
                  {' | '}
                  {formatTrackTimestamp(selectedFuelEventMapRow.eventDate)}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                  {selectedFuelEventCoordinates}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  setSelectedFuelEventMapRow(null);
                  setFuelEventMapError('');
                  clearFuelEventMapArtifacts();
                }}
              >
                Close
              </Button>
            </Stack>

            <Box sx={{
              height: { xs: 250, md: 290 },
              borderRadius: 2.2,
              border: '1px solid rgba(148, 163, 184, 0.18)',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#F8FAFC',
              position: 'relative'
            }}
            >
              <Box ref={fuelEventMapContainerRef} sx={LOCATION_MAP_CONTAINER_STYLE} />

              {(isMapKeyLoading || isMapScriptLoading || !googleMapsKey) && (
                <Box sx={{
                  position: 'absolute',
                  inset: 0,
                  bgcolor: 'rgba(248, 250, 252, 0.82)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none'
                }}
                >
                  <Stack spacing={1} alignItems="center" sx={{ minWidth: 220 }}>
                    <Typography variant="body2" sx={{ color: '#475569', fontWeight: 700 }}>Loading map...</Typography>
                    <LinearProgress sx={{ width: '100%', height: 7, borderRadius: 999 }} />
                  </Stack>
                </Box>
              )}

              {(fuelEventMapError || mapError) && (
                <Box sx={{
                  position: 'absolute',
                  inset: 0,
                  bgcolor: 'rgba(248, 250, 252, 0.9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 1.2
                }}
                >
                  <Typography variant="body2" sx={{ color: '#B91C1C', fontWeight: 700, textAlign: 'center' }}>
                    {fuelEventMapError || mapError}
                  </Typography>
                </Box>
              )}
            </Box>
          </Card>
        )}

        <Card sx={{
          borderRadius: 3, border: '1px solid rgba(148, 163, 184, 0.16)', boxShadow: 'none', overflow: 'hidden'
        }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                  {['Event time', 'Type', 'Volume', 'Tank', 'Duration', 'Address', 'Map'].map((label) => (
                    <TableCell
                      key={label}
                      sx={{
                        py: 1.1, px: 1.4, color: '#475569', fontWeight: 800, borderBottom: '1px solid rgba(148, 163, 184, 0.16)'
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {fuelRows.map((row) => {
                  const isRefilling = row.type === 1;
                  const isDraining = row.type === 3;
                  const hasCoordinates = Number.isFinite(Number(row.latitude)) && Number.isFinite(Number(row.longitude));
                  let chipColor = '#475569';
                  let chipBackground = alpha('#64748B', 0.12);

                  if (isRefilling) {
                    chipColor = '#166534';
                    chipBackground = alpha('#16A34A', 0.12);
                  } else if (isDraining) {
                    chipColor = '#991B1B';
                    chipBackground = alpha('#DC2626', 0.12);
                  }

                  return (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ py: 1.15, px: 1.4, verticalAlign: 'top' }}>
                        <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 700 }}>
                          {formatTrackTimestamp(row.eventDate)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748B' }}>
                          {row.vehicleName}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.15, px: 1.4, verticalAlign: 'top' }}>
                        <Chip
                          size="small"
                          label={row.typeLabel}
                          sx={{ bgcolor: chipBackground, color: chipColor, fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 1.15, px: 1.4, verticalAlign: 'top' }}>
                        <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 800 }}>
                          {formatFuelVolume(row.volume)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.15, px: 1.4, verticalAlign: 'top' }}>
                        <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 700 }}>
                          Tank
                          {' '}
                          {row.tankNumber}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.15, px: 1.4, verticalAlign: 'top' }}>
                        <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 700 }}>
                          {row.durationMinutes !== null ? formatDuration(row.durationMinutes) : '--'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748B' }}>
                          Start:
                          {' '}
                          {formatTrackTimestamp(row.startDate)}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.15,
                          px: 1.4,
                          verticalAlign: 'top',
                          maxWidth: 420
                        }}
                      >
                        <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.45 }}>
                          {row.address}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.15,
                          px: 1.4,
                          verticalAlign: 'top',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {hasCoordinates ? (
                          <Button
                            size="small"
                            variant={selectedFuelEventMapRow?.id === row.id ? 'contained' : 'outlined'}
                            color="primary"
                            startIcon={<PlaceRoundedIcon fontSize="small" />}
                            onClick={() => {
                              setFuelEventMapError('');
                              setSelectedFuelEventMapRow({
                                ...row,
                                latitude: Number(row.latitude),
                                longitude: Number(row.longitude)
                              });
                            }}
                            sx={{ textTransform: 'none', fontWeight: 700 }}
                          >
                            Locate
                          </Button>
                        ) : (
                          <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700 }}>
                            --
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Stack>
    );
  };

  const renderActiveReport = () => {
    if (activeReport === 'location') return renderLocationReport();
    if (activeReport === 'statistics') return renderStatisticsReport();
    if (activeReport === 'fuel') return renderFuelReport();
    if (activeReport === 'trackFuel') return renderTrackReport();
    return renderTrackReport();
  };

  const renderDateTimeFilter = () => (
    <Card sx={{
      p: 1.75, borderRadius: 3.25, border: '1px solid rgba(148, 163, 184, 0.2)', boxShadow: 'none', bgcolor: '#F8FAFC'
    }}
    >
      <Stack spacing={1.35}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0F172A' }}>Date & Time Filter</Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={0.8} sx={{ flexWrap: 'wrap', gap: 0.8 }}>
          {DATE_RANGE_PRESETS.map((preset) => (
            <Chip
              key={preset.value}
              label={preset.label}
              clickable
              onClick={() => handleDatePresetChange(preset.value)}
              variant={dateRangePreset === preset.value ? 'filled' : 'outlined'}
              sx={{
                fontWeight: 700, color: dateRangePreset === preset.value ? '#FFFFFF' : '#334155', bgcolor: dateRangePreset === preset.value ? '#0F172A' : '#FFFFFF', borderColor: 'rgba(148, 163, 184, 0.4)'
              }}
            />
          ))}
        </Stack>

        <Grid container spacing={1.25}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="From date & time"
              type="datetime-local"
              value={dateRange.fromDateTime}
              onChange={(event) => handleDateRangeChange('fromDateTime', event.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: dateRange.toDateTime || undefined }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="To date & time"
              type="datetime-local"
              value={dateRange.toDateTime}
              onChange={(event) => handleDateRangeChange('toDateTime', event.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: dateRange.fromDateTime || undefined }}
            />
          </Grid>
        </Grid>
      </Stack>
    </Card>
  );

  const renderSelectedWorkspace = () => (
    <Card sx={{
      p: { xs: 1.5, md: 2 }, borderRadius: 4, border: '1px solid rgba(148, 163, 184, 0.16)', boxShadow: 'none'
    }}
    >
      <Tabs
        value={activeReport}
        onChange={handleReportChange}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ minHeight: 0, mb: shouldShowDateTimeFilter ? 1.6 : 2, '& .MuiTabs-indicator': { height: 3, borderRadius: 999, bgcolor: '#0F172A' } }}
      >
        {REPORT_TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <Tab
              key={tab.value}
              value={tab.value}
              icon={<Icon fontSize="small" />}
              iconPosition="start"
              label={tab.label}
              sx={{
                minHeight: 0, py: 1.25, px: 1.5, fontWeight: 700, color: '#64748B', '&.Mui-selected': { color: '#0F172A' }
              }}
            />
          );
        })}
      </Tabs>
      {shouldShowDateTimeFilter && (
        <Box sx={{ mb: 1.6 }}>
          {renderDateTimeFilter()}
        </Box>
      )}
      {renderActiveReport()}
    </Card>
  );

  // FIX: Cost Optimization - Workspace Render Layer
  function renderFleetWorkspace() {
    if (isFleetLoading || isVehicleLoading) return renderVehicleLoading();
    if (!vehicles.length) return renderNoFleetData();

    // We intentionally return the workspace even if 0 vehicles are selected while in Location tab.
    // This allows the map to stay alive in DOM.
    if (isLocationSelectionMode || selectedVehicle) {
      return renderSelectedWorkspace();
    }

    return renderNoVehicleSelected();
  }

  return (
    <Box sx={{
      minHeight: '100vh', bgcolor: '#F8FAFC', pt: 2, pb: 8
    }}
    >
      <Container maxWidth="xl">
        <ScreenHeader title="Fleet Management" />
        <Stack spacing={3}>
          <Grid container spacing={2.5} alignItems="flex-start">
            <Grid item xs={12} lg={3} xl={3}>
              {isFleetLoading ? renderFleetHeaderLoading() : renderVehicleSelectionRail()}
            </Grid>
            <Grid item xs={12} lg={9} xl={9}>
              {renderFleetWorkspace()}
            </Grid>
          </Grid>
        </Stack>
      </Container>
    </Box>
  );
};

export default FleetManagement;
